import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.home');
import { OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
    AUTO_SAVE_INTERVAL_MS,
    ArtifactRecord,
    ArtifactSlot,
    applyOfflineProgress,
    applyPrestige,
    buildOfflineReportLines as createOfflineReportLines,
    canPrestige,
    completeResearch,
    formatCompactValue as formatCompactResourceValue,
    formatOfflineHours,
    DiscoveredPlanetRecord,
    evaluateGuideQuests,
    formatGuideQuestRewardLabel,
    formatPercent,
    GameState,
    getActiveGuideQuestDefinition,
    getGuideQuestCategory,
    getGuideQuestCompletedCount,
    getGuideQuestProgress,
    getGuideQuestStatus,
    getLatestPlanet,
    getPartUpgradeCost,
    GUIDE_QUEST_CATEGORIES,
    GUIDE_QUEST_DEFINITIONS,
    GuideQuestCategoryId,
    GuideQuestCompletion,
    GuideQuestDefinition,
    GuideQuestProgress,
    GuideQuestStatus,
    loadGameState,
    markGuideQuestFlag,
    MAX_OFFLINE_SECONDS,
    OfflineReport,
    PART_UPGRADE_DEFINITIONS,
    PLANET_GRADE_DEFINITIONS,
    PRESTIGE_DEFINITIONS,
    ProbeSpecKey,
    RESEARCH_DEFINITIONS,
    ResearchNodeId,
    ResearchStatus,
    resetAllGameDataForDevelopment,
    ResourceAmounts,
    saveGameState,
    TICK_INTERVAL_MS,
    advanceGameByElapsedMs,
    upgradeProbePart
} from "src/app/astrova/game-logic";

type ActiveTab = "exploration" | "research" | "artifacts" | "logs" | "prestige";
type PrestigeDialogMode = "confirm" | "monologue";
type ResourceKey = keyof ResourceAmounts;
type ArtifactKey = ArtifactSlot["artifactKey"];
type TutorialScope = "main" | "research" | "artifact" | "record" | "prestige";
type TutorialHighlight =
    | "header"
    | "resources"
    | "specs"
    | "space"
    | "upgrades"
    | "tabs"
    | "all"
    | "research-panel"
    | "research-data"
    | "research-map"
    | "research-summary"
    | "artifact-panel"
    | "artifact-summary"
    | "artifact-collection"
    | "artifact-detail"
    | "artifact-log"
    | "record-panel"
    | "record-summary"
    | "record-list"
    | "record-detail"
    | "record-timeline"
    | "prestige-panel"
    | "prestige-status"
    | "prestige-progress"
    | "prestige-requirements"
    | "prestige-bonuses"
    | "prestige-history";

interface ResourceView {
    key: ResourceKey;
    name: string;
    amount: string;
    detail: string;
    rate: string;
    tone: string;
}

interface SpecView {
    key: ProbeSpecKey;
    name: string;
    shortName: string;
    level: number;
    effectLabel: string;
    cost: string;
    canUpgrade: boolean;
    highlighted: boolean;
}

interface ResearchTreeNode {
    id: ResearchNodeId;
    name: string;
    description: string;
    status: ResearchStatus;
    statusLabel: string;
    cost: string;
    canResearch: boolean;
    branchLabel: string;
    branchTone: string;
    completedChildren: number;
    totalChildren: number;
    children: ResearchTreeNode[];
}

interface ResearchEffectView {
    key: string;
    label: string;
    value: string;
    active: boolean;
}

interface ArtifactDefinition {
    key: ArtifactKey;
    name: string;
    description: string;
    iconClass: string;
}

interface ArtifactView extends ArtifactDefinition {
    discovered: boolean;
    count: number;
    rarity: string;
    foundAt: string;
    sourcePlanet: DiscoveredPlanetRecord | null;
}

interface ArtifactLogEntryView {
    id: string;
    name: string;
    iconClass: string;
    meta: string;
    foundAt: string;
}

interface LogEntryView {
    id: string;
    title: string;
    meta: string;
    result: string;
    statusLabel: string;
    outcome: DiscoveredPlanetRecord["outcome"];
    grade: DiscoveredPlanetRecord["grade"];
    typeLabel: string;
    lifeSignalLabel: string;
    hazardLabel: string;
    discoveredAt: string;
    completedAt: string;
    dataGained: string;
    partsGained: string;
    artifactCount: number;
    seed: string;
}

interface RecordSummaryView {
    key: string;
    label: string;
    value: string;
    tone: string;
}

interface PrestigeLogEntryView {
    id: string;
    stageLabel: string;
    systemName: string;
    bonusLabel: string;
    date: string;
}

interface PrestigeRequirementView {
    key: string;
    label: string;
    value: string;
    detail: string;
    complete: boolean;
}

interface PrestigeBonusView {
    key: string;
    label: string;
    value: string;
    active: boolean;
}

interface ExpeditionResultView {
    title: string;
    planetName: string;
    detail: string;
    failed: boolean;
}

interface QuestView {
    id: number;
    title: string;
    categoryName: string;
    categoryIcon: string;
    status: GuideQuestStatus;
    progress: GuideQuestProgress;
    progressLabel: string;
    rewardLabel: string;
}

interface QuestCategoryView {
    id: GuideQuestCategoryId;
    name: string;
    icon: string;
    completedCount: number;
    totalCount: number;
    quests: QuestView[];
}

interface QuestCompletionPopupView {
    title: string;
    categoryName: string;
    rewardLabel: string;
    progressLabel: string;
}

interface TutorialStep {
    highlight: TutorialHighlight;
    message: string;
}

const TUTORIAL_STORAGE_KEY = "tutorial_done";
const RESEARCH_TUTORIAL_STORAGE_KEY = "research_tutorial_done";
const ARTIFACT_TUTORIAL_STORAGE_KEY = "artifact_tutorial_done";
const RECORD_TUTORIAL_STORAGE_KEY = "record_tutorial_done";
const PRESTIGE_TUTORIAL_STORAGE_KEY = "prestige_tutorial_done";
const ACCESS_PROFILE_STORAGE_KEY = "astrova.accessProfile.v1";

const TUTORIAL_STEPS: TutorialStep[] = [
    {
        highlight: "header",
        message: "나는 ASTROVA-1. 인류의 마지막 탐사선이야.\n현재 스테이지와 탐사 현황을 여기서 확인할 수 있어."
    },
    {
        highlight: "resources",
        message: "탐사 데이터, 에너지, 부품. 이 세 가지가 핵심 자원이야.\n자동으로 쌓이니까 가끔 접속해서 확인해줘."
    },
    {
        highlight: "specs",
        message: "내 탐사 능력치야. 엔진이 빠를수록 더 빨리 행성에 도착해.\n방어막이 부족하면 위험한 행성에서 실패할 수 있어."
    },
    {
        highlight: "space",
        message: "지금 내가 행성을 향해 날아가고 있어.\n도착하면 자원을 수집하고 다음 행성으로 자동으로 출발해."
    },
    {
        highlight: "upgrades",
        message: "부품이 쌓이면 여기서 스펙을 강화할 수 있어.\n강화할수록 더 높은 등급의 행성에 갈 수 있어."
    },
    {
        highlight: "tabs",
        message: "연구, 유물, 기록, 프레스티지 탭도 있어.\n천천히 하나씩 열어봐."
    },
    {
        highlight: "all",
        message: "이제 탐사를 시작할게. 잠깐 자리를 비워도 괜찮아.\n나는 계속 날아가고 있을 테니까."
    }
];

const RESEARCH_TUTORIAL_STEPS: TutorialStep[] = [
    {
        highlight: "research-panel",
        message: "연구 배열에 진입했어.\n탐사 데이터로 장기 성능을 열어 다음 행성 탐사를 유리하게 만들 수 있어."
    },
    {
        highlight: "research-data",
        message: "연구에는 탐사 데이터가 필요해.\n오른쪽 DATA 표시를 보고 지금 연구 가능한지 먼저 판단하면 돼."
    },
    {
        highlight: "research-map",
        message: "밝게 표시된 노드는 지금 연구할 수 있는 기술이야.\n선행 연구를 끝내면 아래 가지가 차례로 개방돼."
    },
    {
        highlight: "research-summary",
        message: "진행률과 활성 효과는 여기서 확인해.\n연구 효과는 탐사 속도, 자원 획득, 생명체와 유물 발견에 바로 반영돼."
    }
];

const ARTIFACT_TUTORIAL_STEPS: TutorialStep[] = [
    {
        highlight: "artifact-panel",
        message: "유물 보관소에 진입했어.\n탐사 중 발견한 희귀 표본과 그 출처를 여기서 추적할 수 있어."
    },
    {
        highlight: "artifact-summary",
        message: "상단 요약은 보유 유물 수, 발견한 종류, 현재 유물 감지 확률을 보여줘.\nSIGNAL 값이 높을수록 다음 탐사에서 유물을 찾을 가능성이 커져."
    },
    {
        highlight: "artifact-collection",
        message: "왼쪽 목록은 유물 종류별 보관 슬롯이야.\n발견한 유물을 선택하면 상세 기록이 갱신되고, 잠긴 슬롯은 아직 신호를 찾지 못한 상태야."
    },
    {
        highlight: "artifact-detail",
        message: "선택한 유물의 설명, 희귀도, 최초 발견 행성 정보를 확인할 수 있어.\n어떤 등급과 생명 신호에서 발견됐는지도 함께 기록돼."
    },
    {
        highlight: "artifact-log",
        message: "최근 발견 로그는 새 유물이 들어온 순서대로 쌓여.\n프레스티지 후에도 유물 컬렉션은 유지되니까 장기 목표로 추적해줘."
    }
];

const RECORD_TUTORIAL_STEPS: TutorialStep[] = [
    {
        highlight: "record-panel",
        message: "탐사 기록 보관소에 진입했어.\n완료한 행성, 실패한 탐사, 항성계 도약 기록을 여기서 다시 확인할 수 있어."
    },
    {
        highlight: "record-summary",
        message: "상단 요약은 누적 로그 수, 성공률, 실패 수, S/X등급 발견 수를 압축해서 보여줘.\n현재 항해가 얼마나 안정적인지 빠르게 판단할 수 있어."
    },
    {
        highlight: "record-list",
        message: "PLANET LOG는 최근 탐사 결과가 쌓이는 목록이야.\n행성을 선택하면 획득 자원, 유물 수, 완료 시각을 기준으로 상세 정보가 갱신돼."
    },
    {
        highlight: "record-detail",
        message: "선택한 행성의 좌표 시드, 타입, 생명 신호, 위험 요소가 여기에 남아.\n아직 기록이 없다면 다음 탐사 완료 후 자동으로 채워질 거야."
    },
    {
        highlight: "record-timeline",
        message: "프레스티지 기록은 항성계를 넘어간 순서대로 보존돼.\n리셋 후에도 탐사 기록과 유물 컬렉션은 장기 항해의 증거로 남아."
    }
];

const PRESTIGE_TUTORIAL_STEPS: TutorialStep[] = [
    {
        highlight: "prestige-panel",
        message: "프레스티지 게이트에 진입했어.\n항성계를 돌파하면 현재 런을 초기화하고 영구 보너스를 받을 수 있어."
    },
    {
        highlight: "prestige-status",
        message: "상단 상태는 프레스티지를 실행할 수 있는지 보여줘.\nREADY가 되기 전까지는 남은 조건을 먼저 채워야 해."
    },
    {
        highlight: "prestige-progress",
        message: "진행률과 다음 목적지는 여기서 확인해.\n조건을 모두 만족하면 프레스티지 실행 버튼으로 다음 항성계로 넘어갈 수 있어."
    },
    {
        highlight: "prestige-requirements",
        message: "REQUIREMENTS는 남은 행성 클리어와 S등급 돌파 여부를 추적해.\n초록색으로 바뀐 조건은 이미 완료된 상태야."
    },
    {
        highlight: "prestige-bonuses",
        message: "NEXT BONUS는 이번 프레스티지로 받을 효과야.\nPERMANENT BONUSES는 이전 리셋에서 얻은 영구 효과를 계속 보여줘."
    },
    {
        highlight: "prestige-history",
        message: "STAR SYSTEMS에는 완료한 항성계와 리셋 기록이 남아.\n프레스티지 후에도 기록과 유물 컬렉션은 장기 항해 기록으로 유지돼."
    }
];

const SPEC_LABELS: Record<ProbeSpecKey, { name: string; shortName: string }> = {
    engine: { name: "엔진", shortName: "ENG" },
    explorationGear: { name: "탐사 장비", shortName: "SCAN" },
    shield: { name: "방어막", shortName: "SHD" },
    antenna: { name: "안테나", shortName: "ANT" }
};

const RESEARCH_TREE: Record<ResearchNodeId, ResearchNodeId[]> = {
    basic_exploration: ["planet_analysis", "high_speed_engine", "deep_space_exploration"],
    planet_analysis: [
        "spectral_mapping",
        "mineral_survey",
        "adaptive_drilling",
        "thermal_refinery",
        "quantum_harvest",
        "planetary_cartography",
        "stellar_logistics",
        "matter_reclaimer",
        "cosmic_extraction",
        "terraforming_index",
        "biosphere_simulation",
        "orbital_foundry",
        "dark_matter_sieve",
        "stellar_compression",
        "nano_probe_swarm",
        "antimatter_logistics",
        "proto_world_engine",
        "genesis_harvest"
    ],
    high_speed_engine: [
        "ion_thrusters",
        "trajectory_ai",
        "gravity_slingshot",
        "warp_calibration",
        "subspace_sails",
        "tachyon_drive",
        "relativistic_navigation",
        "zero_point_engine",
        "event_horizon_drive",
        "lightfold_engine",
        "photon_lattice",
        "inertial_nullifier",
        "wormhole_geometry",
        "causality_buffer",
        "quantum_wake_drive",
        "horizon_skimming",
        "singularity_pilot",
        "eternity_engine"
    ],
    deep_space_exploration: [
        "life_detection",
        "artifact_scanner",
        "hazard_prediction",
        "void_signal_array",
        "bio_signature_archive",
        "ancient_translation",
        "xeno_ecology",
        "relic_resonator",
        "deep_space_beacon",
        "multiverse_sensor",
        "forbidden_atlas",
        "origin_signal",
        "sentient_signal_model",
        "relic_memory_core",
        "living_world_index",
        "mythic_archive",
        "first_contact_protocol",
        "precursor_gateway",
        "cosmic_language",
        "omega_observatory"
    ],
    life_detection: [],
    artifact_scanner: [],
    spectral_mapping: [],
    mineral_survey: [],
    adaptive_drilling: [],
    thermal_refinery: [],
    quantum_harvest: [],
    planetary_cartography: [],
    stellar_logistics: [],
    matter_reclaimer: [],
    cosmic_extraction: [],
    terraforming_index: [],
    biosphere_simulation: [],
    orbital_foundry: [],
    dark_matter_sieve: [],
    stellar_compression: [],
    nano_probe_swarm: [],
    antimatter_logistics: [],
    proto_world_engine: [],
    genesis_harvest: [],
    ion_thrusters: [],
    trajectory_ai: [],
    gravity_slingshot: [],
    warp_calibration: [],
    subspace_sails: [],
    tachyon_drive: [],
    relativistic_navigation: [],
    zero_point_engine: [],
    event_horizon_drive: [],
    lightfold_engine: [],
    hazard_prediction: [],
    void_signal_array: [],
    bio_signature_archive: [],
    ancient_translation: [],
    xeno_ecology: [],
    relic_resonator: [],
    deep_space_beacon: [],
    multiverse_sensor: [],
    forbidden_atlas: [],
    origin_signal: [],
    photon_lattice: [],
    inertial_nullifier: [],
    wormhole_geometry: [],
    causality_buffer: [],
    quantum_wake_drive: [],
    horizon_skimming: [],
    singularity_pilot: [],
    eternity_engine: [],
    sentient_signal_model: [],
    relic_memory_core: [],
    living_world_index: [],
    mythic_archive: [],
    first_contact_protocol: [],
    precursor_gateway: [],
    cosmic_language: [],
    omega_observatory: []
};

const ARTIFACT_DEFINITIONS: ArtifactDefinition[] = [
    {
        key: "ancient_ruins",
        name: "고대 문명 흔적",
        description: "거대한 방주가 남긴 좌표 조각",
        iconClass: "artifact-icon--ruins"
    },
    {
        key: "black_box",
        name: "블랙박스",
        description: "소멸한 탐사선의 마지막 항로 기록",
        iconClass: "artifact-icon--box"
    },
    {
        key: "crystal",
        name: "크리스탈",
        description: "성간 방사선을 기억하는 투명한 결정",
        iconClass: "artifact-icon--crystal"
    }
];

const PLANET_TYPE_LABELS: Record<string, string> = {
    ice: "얼음",
    lava: "용암",
    gas: "가스",
    desert: "사막",
    ocean: "바다",
    forest: "초목",
    alien: "외계"
};

const LIFE_SIGNAL_LABELS: Record<string, string> = {
    none: "없음",
    microbial: "미생물",
    intelligent: "지적 생명체"
};

const HAZARD_LABELS: Record<string, string> = {
    radiation: "방사능",
    gravity_anomaly: "중력 이상",
    hostile_life: "적대 생명체"
};

const STAR_SYSTEM_LABELS: Record<string, string> = {
    solar_system: "태양계",
    alpha_centauri: "알파 센타우리",
    orion_nebula: "오리온 성운",
    galactic_core: "은하 중심부",
    multiverse: "다중우주"
};

@Component({
    selector: 'wiz-page-home',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.home/view.scss */
:host {
  display: block;
  width: 100%;
  height: 100%;
  color: #dff6ff;
  background: #08091a;
  font-family: "Press Start 2P", SUIT, monospace;
}

* {
  box-sizing: border-box;
}

button {
  font: inherit;
  letter-spacing: 0;
}

h1,
h2,
p {
  margin: 0;
}

.astrova-shell {
  display: grid;
  grid-template-rows: 70px 64px minmax(0, 1fr) 68px;
  gap: 10px;
  width: 100%;
  height: 100vh;
  min-height: 720px;
  overflow: hidden;
  padding: 12px;
  background: #08091a;
}

.top-header,
.resource-bar,
.side-panel,
.space-view,
.artifact-view,
.research-view,
.record-view,
.prestige-view,
.bottom-tabs button {
  border: 2px solid #1a2030;
  background: #0d1525;
  box-shadow: inset 0 0 0 2px rgba(126, 207, 255, 0.05);
}

.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
}

.header-block {
  display: grid;
  gap: 7px;
  min-width: 0;
}

.top-header h1 {
  color: #7ecfff;
  font-size: 22px;
  line-height: 1;
}

.stage-line {
  min-width: 0;
  overflow: hidden;
  color: #ffd966;
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.online-line {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #4aff8a;
  font-size: 9px;
}

.online-dot {
  width: 9px;
  height: 9px;
  background: #4aff8a;
  box-shadow: 0 0 12px rgba(74, 255, 138, 0.75);
  animation: onlineBlink 1.4s steps(2) infinite;
}

.tutorial-replay {
  display: grid;
  flex: 0 0 auto;
  min-width: 132px;
  border: 2px solid #7ecfff;
  padding: 9px 12px;
  color: #dff6ff;
  text-align: left;
  background: #0a1324;
  box-shadow: inset 0 0 0 2px rgba(126, 207, 255, 0.12), 0 0 16px rgba(126, 207, 255, 0.12);
  cursor: pointer;
}

.tutorial-replay:hover,
.tutorial-replay:focus-visible {
  border-color: #ffd966;
  color: #ffffff;
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(255, 217, 102, 0.18), 0 0 18px rgba(255, 217, 102, 0.18);
}

.tutorial-replay span {
  color: #7ecfff;
  font-size: 8px;
  line-height: 1.2;
}

.tutorial-replay strong {
  margin-top: 4px;
  color: #ffd966;
  font-size: 10px;
  line-height: 1.2;
}

.resource-bar {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  overflow: hidden;
}

.resource-cell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  align-content: center;
  gap: 7px 10px;
  min-width: 0;
  padding: 12px 16px;
}

.resource-cell + .resource-cell {
  border-left: 2px solid #1a2030;
}

.resource-name {
  min-width: 0;
  overflow: hidden;
  color: #7ecfff;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-cell strong {
  color: #ffd966;
  font-size: 16px;
  line-height: 1;
}

.resource-cell em {
  grid-column: 1/-1;
  color: #4aff8a;
  font-size: 9px;
  font-style: normal;
}

.tone-energy strong {
  color: #ffd966;
}

.tone-parts strong {
  color: #ffb45f;
}

.main-area {
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 10px;
  min-height: 0;
}

.side-panel {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 10px;
}

.panel-heading {
  border-bottom: 2px solid #1a2030;
  padding-bottom: 9px;
  color: #7ecfff;
  font-size: 10px;
  line-height: 1;
}

.panel-heading-split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.panel-heading-split strong {
  color: #ffd966;
  font-size: 9px;
}

.spec-list {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}

.spec-card {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 2px solid #1a2030;
  padding: 8px;
  background: #080f1d;
}

.spec-card.is-warning {
  border-color: #ff6b6b;
  background: rgba(255, 107, 107, 0.12);
  animation: warningPulse 0.95s steps(2) infinite;
}

.spec-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 5px;
  align-items: center;
}

.spec-row strong {
  min-width: 0;
  overflow: hidden;
  color: #eaf9ff;
  font-size: 10px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spec-row span,
.spec-card small,
.shield-warning {
  font-size: 9px;
  line-height: 1.3;
}

.spec-row span {
  color: #ffd966;
}

.spec-card small {
  color: #6f86a6;
}

.spec-upgrade-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.spec-upgrade-row span {
  overflow: hidden;
  color: #ffd966;
  font-size: 9px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shield-warning {
  color: #ff6b6b;
}

.gauge {
  height: 9px;
  overflow: hidden;
  border: 1px solid #263149;
  background: #050814;
}

.gauge span {
  display: block;
  height: 100%;
  min-width: 3px;
  background: repeating-linear-gradient(90deg, #7ecfff 0 7px, #4f9ed0 7px 14px);
  box-shadow: 0 0 10px rgba(126, 207, 255, 0.5);
}

.spec-card.is-warning .gauge span {
  background: repeating-linear-gradient(90deg, #ff6b6b 0 7px, #b93f4b 7px 14px);
}

.space-view,
.artifact-view,
.research-view,
.record-view,
.prestige-view {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  image-rendering: pixelated;
  background: linear-gradient(rgba(126, 207, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(126, 207, 255, 0.035) 1px, transparent 1px), #050714;
  background-size: 28px 28px;
}

.space-view {
  --planet-accent: #7ecfff;
  --planet-accent-glow: rgba(126, 207, 255, 0.42);
  background: radial-gradient(circle at 74% 18%, rgba(126, 207, 255, 0.1) 0 7%, transparent 8%), linear-gradient(rgba(126, 207, 255, 0.032) 1px, transparent 1px), linear-gradient(90deg, rgba(126, 207, 255, 0.032) 1px, transparent 1px), linear-gradient(180deg, #050714, #071226);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.space-view::before,
.space-view::after {
  position: absolute;
  z-index: 0;
  pointer-events: none;
  content: "";
  image-rendering: pixelated;
}

.space-view::before {
  inset: 0;
  background: repeating-linear-gradient(0deg, transparent 0 27px, rgba(126, 207, 255, 0.025) 27px 28px), repeating-linear-gradient(90deg, transparent 0 27px, rgba(126, 207, 255, 0.025) 27px 28px);
  opacity: 0.7;
}

.space-view::after {
  left: 6%;
  top: 16%;
  width: 8px;
  height: 8px;
  background: rgba(126, 207, 255, 0.24);
  box-shadow: 34px 12px rgba(255, 217, 102, 0.2), 78px -6px rgba(126, 207, 255, 0.18), 118px 18px rgba(74, 255, 138, 0.16), 482px 44px rgba(126, 207, 255, 0.18), 520px 58px rgba(255, 217, 102, 0.16), 732px 312px rgba(126, 207, 255, 0.18), 782px 330px rgba(74, 255, 138, 0.14), 944px 206px rgba(255, 217, 102, 0.16);
  opacity: 0.58;
}

.planet-scene {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
}

.planet-scene::before,
.planet-scene::after {
  position: absolute;
  z-index: 8;
  pointer-events: none;
  content: "";
}

.planet-scene span {
  position: absolute;
  display: block;
  image-rendering: pixelated;
}

.scene-layer {
  inset: 0;
}

.layer-sky {
  z-index: 2;
}

.layer-atmosphere {
  z-index: 1;
}

.layer-horizon {
  z-index: 3;
}

.layer-back {
  z-index: 4;
}

.layer-ground {
  z-index: 5;
}

.layer-front {
  z-index: 6;
}

.scene-prop {
  z-index: 7;
}

.scene-layer::before,
.scene-layer::after,
.scene-prop::before,
.scene-prop::after {
  position: absolute;
  display: block;
  content: "";
  image-rendering: pixelated;
}

.prop-d {
  display: none;
}

.prop-e,
.prop-f,
.prop-g {
  display: block;
}

.planet-theme-ice {
  --planet-accent: #a6efff;
  --planet-accent-glow: rgba(166, 239, 255, 0.5);
  background: radial-gradient(circle at 78% 20%, rgba(223, 246, 255, 0.16) 0 9%, transparent 10%), linear-gradient(rgba(166, 239, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(166, 239, 255, 0.04) 1px, transparent 1px), linear-gradient(180deg, #04101f 0%, #082740 100%);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-ice .layer-sky {
  left: 4%;
  top: 4%;
  width: 72%;
  height: 32%;
  background: linear-gradient(118deg, transparent 0 24%, rgba(117, 244, 223, 0.18) 24% 30%, transparent 30% 42%, rgba(223, 246, 255, 0.14) 42% 48%, transparent 48%), linear-gradient(130deg, transparent 0 32%, rgba(126, 207, 255, 0.16) 32% 36%, transparent 36%);
  opacity: 0.9;
}

.planet-theme-ice .layer-back {
  right: 7%;
  bottom: 23%;
  left: 5%;
  height: 28%;
  background: linear-gradient(180deg, rgba(166, 239, 255, 0.38), rgba(52, 133, 171, 0.34));
  clip-path: polygon(0 86%, 8% 44%, 15% 76%, 24% 22%, 32% 80%, 42% 36%, 52% 76%, 64% 18%, 74% 82%, 85% 46%, 100% 90%, 100% 100%, 0 100%);
}

.planet-theme-ice .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 34%;
  background: repeating-linear-gradient(90deg, transparent 0 18px, rgba(223, 246, 255, 0.22) 18px 20px, transparent 20px 46px), linear-gradient(180deg, rgba(223, 246, 255, 0.76), rgba(82, 161, 199, 0.42));
  clip-path: polygon(0 38%, 13% 31%, 27% 40%, 42% 28%, 57% 36%, 72% 26%, 86% 34%, 100% 25%, 100% 100%, 0 100%);
}

.planet-theme-ice .prop-a {
  left: 13%;
  bottom: 26%;
  width: 36px;
  height: 96px;
  background: #dff6ff;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  box-shadow: 54px 18px 0 -2px rgba(166, 239, 255, 0.8), 114px 30px 0 -7px rgba(223, 246, 255, 0.72), 710px 26px 0 -4px rgba(166, 239, 255, 0.72);
}

.planet-theme-ice .prop-b {
  left: 16%;
  top: 10%;
  width: 4px;
  height: 4px;
  background: rgba(223, 246, 255, 0.82);
  box-shadow: 108px 42px rgba(223, 246, 255, 0.76), 226px 12px rgba(166, 239, 255, 0.66), 386px 70px rgba(223, 246, 255, 0.68), 582px 36px rgba(166, 239, 255, 0.64), 764px 82px rgba(223, 246, 255, 0.62), 928px 28px rgba(166, 239, 255, 0.58);
  animation: snowFall 5s steps(6) infinite;
}

.planet-theme-ice .prop-c {
  right: 18%;
  bottom: 13%;
  width: 210px;
  height: 8px;
  background: rgba(4, 16, 31, 0.38);
  box-shadow: 28px 18px rgba(4, 16, 31, 0.28), -84px 30px rgba(4, 16, 31, 0.3);
  transform: skewX(-28deg);
}

.planet-theme-lava {
  --planet-accent: #ff9c3f;
  --planet-accent-glow: rgba(255, 156, 63, 0.5);
  background: radial-gradient(circle at 22% 18%, rgba(255, 156, 63, 0.17) 0 10%, transparent 11%), linear-gradient(rgba(255, 107, 107, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 107, 107, 0.04) 1px, transparent 1px), linear-gradient(180deg, #14070b, #2a0c12 68%, #130408);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-lava .layer-sky {
  left: 4%;
  top: 8%;
  width: 86%;
  height: 44%;
  background: radial-gradient(circle at 20% 60%, rgba(255, 156, 63, 0.14) 0 7%, transparent 8%), radial-gradient(circle at 72% 30%, rgba(255, 107, 107, 0.12) 0 9%, transparent 10%), repeating-linear-gradient(165deg, transparent 0 58px, rgba(255, 180, 95, 0.08) 58px 64px, transparent 64px 116px);
}

.planet-theme-lava .layer-back {
  left: 7%;
  bottom: 24%;
  width: 34%;
  height: 42%;
  background: linear-gradient(180deg, #5c2421, #2a0c12);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  box-shadow: 92px 42px 0 -20px rgba(82, 26, 22, 0.85);
}

.planet-theme-lava .layer-back::before {
  position: absolute;
  top: 18px;
  left: 50%;
  width: 70px;
  height: 24px;
  background: #ff9c3f;
  box-shadow: 0 14px 0 rgba(255, 107, 107, 0.72);
  content: "";
  transform: translateX(-50%);
}

.planet-theme-lava .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 32%;
  background: repeating-linear-gradient(90deg, transparent 0 30px, rgba(255, 156, 63, 0.24) 30px 34px, transparent 34px 58px), linear-gradient(180deg, rgba(108, 37, 28, 0.82), rgba(35, 7, 13, 0.96));
  clip-path: polygon(0 46%, 16% 35%, 31% 48%, 47% 32%, 63% 44%, 82% 28%, 100% 38%, 100% 100%, 0 100%);
}

.planet-theme-lava .prop-a {
  left: 35%;
  bottom: 3%;
  width: 140px;
  height: 160px;
  background: linear-gradient(90deg, transparent 0 26%, #ff9c3f 26% 42%, transparent 42% 100%);
  transform: rotate(42deg);
  filter: drop-shadow(0 0 10px rgba(255, 156, 63, 0.44));
}

.planet-theme-lava .prop-b {
  right: 13%;
  bottom: 20%;
  width: 34px;
  height: 18px;
  background: #4b1719;
  box-shadow: 48px 24px #321014, -62px 36px #5c2421, 132px 42px #321014, 240px 12px #4b1719;
}

.planet-theme-lava .prop-c {
  left: 18%;
  top: 18%;
  width: 5px;
  height: 5px;
  background: #ffd966;
  box-shadow: 80px 42px #ff9c3f, 182px 12px #ff6b6b, 334px 72px #ffd966, 504px 28px #ff9c3f, 722px 86px #ff6b6b, 914px 34px #ffd966;
  animation: lavaPulse 1.2s steps(2) infinite;
}

.planet-theme-gas {
  --planet-accent: #a889ff;
  --planet-accent-glow: rgba(168, 137, 255, 0.5);
  background: radial-gradient(circle at 68% 24%, rgba(255, 126, 168, 0.14) 0 12%, transparent 13%), linear-gradient(rgba(168, 137, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(168, 137, 255, 0.04) 1px, transparent 1px), linear-gradient(180deg, #0b0824, #182049);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-gas .layer-sky {
  inset: 0;
  background: repeating-linear-gradient(-7deg, transparent 0 38px, rgba(117, 244, 223, 0.14) 38px 56px, transparent 56px 92px), repeating-linear-gradient(8deg, transparent 0 58px, rgba(168, 137, 255, 0.13) 58px 80px, transparent 80px 132px);
  opacity: 0.8;
}

.planet-theme-gas .layer-back {
  right: 9%;
  bottom: 22%;
  width: 260px;
  height: 120px;
  border: 8px solid rgba(117, 244, 223, 0.26);
  border-left-color: transparent;
  border-radius: 50%;
  transform: rotate(-11deg);
}

.planet-theme-gas .layer-ground {
  left: -4%;
  right: -4%;
  bottom: -2%;
  height: 31%;
  background: linear-gradient(180deg, rgba(168, 137, 255, 0.02), rgba(14, 15, 54, 0.82)), repeating-linear-gradient(90deg, rgba(117, 244, 223, 0.14) 0 24px, transparent 24px 70px);
  clip-path: polygon(0 52%, 9% 40%, 20% 48%, 32% 36%, 45% 46%, 59% 34%, 74% 48%, 88% 38%, 100% 50%, 100% 100%, 0 100%);
}

.planet-theme-gas .prop-a {
  left: 16%;
  bottom: 40%;
  width: 120px;
  height: 24px;
  background: rgba(117, 244, 223, 0.36);
  box-shadow: 18px 18px rgba(168, 137, 255, 0.26), 384px -42px rgba(117, 244, 223, 0.24), 426px -22px rgba(255, 126, 168, 0.2), 644px 28px rgba(168, 137, 255, 0.24);
  animation: gasFloat 6s steps(8) infinite;
}

.planet-theme-gas .prop-b {
  left: 42%;
  top: 34%;
  width: 130px;
  height: 60px;
  border: 6px solid rgba(255, 126, 168, 0.52);
  border-left-color: rgba(117, 244, 223, 0.42);
  border-radius: 50%;
  box-shadow: inset 22px 0 0 rgba(168, 137, 255, 0.18);
}

.planet-theme-gas .prop-c {
  right: 23%;
  bottom: 31%;
  width: 24px;
  height: 72px;
  background: rgba(117, 244, 223, 0.42);
  box-shadow: -26px 20px rgba(168, 137, 255, 0.34), 30px 14px rgba(255, 126, 168, 0.28);
}

.planet-theme-desert {
  --planet-accent: #ffca7a;
  --planet-accent-glow: rgba(255, 202, 122, 0.46);
  background: radial-gradient(circle at 16% 18%, rgba(255, 202, 122, 0.16) 0 10%, transparent 11%), linear-gradient(rgba(255, 202, 122, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 202, 122, 0.04) 1px, transparent 1px), linear-gradient(180deg, #120e08, #2b2314);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-desert .layer-sky {
  right: 0;
  bottom: 30%;
  left: 0;
  height: 38%;
  background: linear-gradient(9deg, transparent 0 48%, rgba(255, 202, 122, 0.13) 48% 52%, transparent 52%), linear-gradient(-5deg, transparent 0 42%, rgba(255, 217, 102, 0.1) 42% 46%, transparent 46%);
}

.planet-theme-desert .layer-back {
  left: 5%;
  bottom: 30%;
  width: 58%;
  height: 24%;
  background: rgba(116, 76, 38, 0.58);
  clip-path: polygon(0 74%, 8% 34%, 18% 34%, 23% 58%, 32% 58%, 38% 22%, 51% 22%, 57% 62%, 70% 62%, 77% 40%, 88% 40%, 100% 78%, 100% 100%, 0 100%);
}

.planet-theme-desert .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 39%;
  background: repeating-linear-gradient(172deg, transparent 0 36px, rgba(255, 217, 102, 0.14) 36px 42px, transparent 42px 76px), linear-gradient(180deg, rgba(255, 202, 122, 0.52), rgba(84, 54, 24, 0.84));
  clip-path: polygon(0 36%, 13% 48%, 31% 32%, 49% 44%, 68% 29%, 85% 42%, 100% 30%, 100% 100%, 0 100%);
}

.planet-theme-desert .prop-a {
  left: 18%;
  bottom: 29%;
  width: 96px;
  height: 96px;
  background: #d1a765;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  box-shadow: 112px 16px 0 -24px rgba(255, 202, 122, 0.72), 142px 42px 0 -34px rgba(255, 202, 122, 0.7);
}

.planet-theme-desert .prop-b {
  right: 20%;
  bottom: 22%;
  width: 18px;
  height: 86px;
  background: rgba(255, 202, 122, 0.48);
  box-shadow: 16px 0 rgba(255, 202, 122, 0.28), 52px 36px rgba(255, 202, 122, 0.3), 70px 36px rgba(255, 202, 122, 0.24);
}

.planet-theme-desert .prop-c {
  left: 9%;
  bottom: 18%;
  width: 180px;
  height: 8px;
  background: rgba(255, 217, 102, 0.3);
  box-shadow: 320px 34px rgba(255, 202, 122, 0.24), 594px 8px rgba(255, 217, 102, 0.2);
  animation: dustDrift 8s steps(8) infinite;
}

.planet-theme-ocean {
  --planet-accent: #75f4df;
  --planet-accent-glow: rgba(117, 244, 223, 0.48);
  background: radial-gradient(circle at 76% 18%, rgba(117, 244, 223, 0.16) 0 9%, transparent 10%), linear-gradient(rgba(40, 127, 214, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(40, 127, 214, 0.05) 1px, transparent 1px), linear-gradient(180deg, #031021, #052846);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-ocean .layer-sky {
  inset: 0;
  background: linear-gradient(180deg, transparent 0 50%, rgba(117, 244, 223, 0.08) 50% 52%, transparent 52%), radial-gradient(circle at 18% 32%, rgba(126, 207, 255, 0.12) 0 6%, transparent 7%);
}

.planet-theme-ocean .layer-back {
  right: 0;
  bottom: 30%;
  left: 0;
  height: 14%;
  background: repeating-linear-gradient(90deg, rgba(117, 244, 223, 0.28) 0 34px, rgba(40, 127, 214, 0.1) 34px 80px);
  clip-path: polygon(0 46%, 9% 60%, 19% 40%, 31% 58%, 43% 42%, 56% 60%, 70% 38%, 84% 58%, 100% 42%, 100% 100%, 0 100%);
  animation: oceanWave 5s steps(6) infinite;
}

.planet-theme-ocean .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 35%;
  background: repeating-linear-gradient(180deg, rgba(117, 244, 223, 0.16) 0 8px, transparent 8px 24px), linear-gradient(180deg, rgba(40, 127, 214, 0.68), rgba(3, 22, 38, 0.92));
  clip-path: polygon(0 22%, 12% 34%, 25% 22%, 38% 36%, 52% 24%, 66% 34%, 81% 20%, 100% 31%, 100% 100%, 0 100%);
}

.planet-theme-ocean .prop-a {
  left: 17%;
  bottom: 29%;
  width: 150px;
  height: 44px;
  background: #244c3f;
  clip-path: polygon(0 82%, 18% 28%, 48% 10%, 78% 32%, 100% 82%, 100% 100%, 0 100%);
  box-shadow: 30px -22px 0 -16px #4aff8a;
}

.planet-theme-ocean .prop-b {
  right: 17%;
  bottom: 18%;
  width: 10px;
  height: 10px;
  background: rgba(117, 244, 223, 0.62);
  box-shadow: -30px -44px rgba(117, 244, 223, 0.46), 24px -76px rgba(126, 207, 255, 0.42), 72px -28px rgba(117, 244, 223, 0.4), 118px -92px rgba(126, 207, 255, 0.34);
  animation: bubbleRise 4.5s steps(8) infinite;
}

.planet-theme-ocean .prop-c {
  left: 46%;
  bottom: 12%;
  width: 14px;
  height: 48px;
  background: #ff7ea8;
  box-shadow: -20px 18px #75f4df, 22px 12px #ffca7a, 44px 28px #75f4df;
}

.planet-theme-forest {
  --planet-accent: #4aff8a;
  --planet-accent-glow: rgba(74, 255, 138, 0.46);
  background: radial-gradient(circle at 74% 18%, rgba(255, 217, 102, 0.12) 0 9%, transparent 10%), linear-gradient(rgba(74, 255, 138, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(74, 255, 138, 0.04) 1px, transparent 1px), linear-gradient(180deg, #03151a, #0a2a23);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-forest .layer-sky {
  top: 0;
  right: 0;
  left: 0;
  height: 30%;
  background: radial-gradient(circle at 8% 70%, rgba(74, 255, 138, 0.16) 0 10%, transparent 11%), radial-gradient(circle at 26% 52%, rgba(117, 244, 223, 0.14) 0 11%, transparent 12%), radial-gradient(circle at 58% 60%, rgba(74, 255, 138, 0.12) 0 12%, transparent 13%), radial-gradient(circle at 84% 54%, rgba(117, 244, 223, 0.12) 0 10%, transparent 11%);
}

.planet-theme-forest .layer-back {
  right: 0;
  bottom: 28%;
  left: 0;
  height: 34%;
  background: repeating-linear-gradient(90deg, rgba(44, 126, 77, 0.52) 0 22px, transparent 22px 54px), linear-gradient(180deg, rgba(74, 255, 138, 0.18), rgba(5, 38, 30, 0.7));
  clip-path: polygon(0 74%, 6% 30%, 13% 74%, 22% 26%, 32% 78%, 42% 34%, 51% 76%, 63% 22%, 74% 78%, 84% 32%, 94% 76%, 100% 48%, 100% 100%, 0 100%);
}

.planet-theme-forest .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 35%;
  background: repeating-linear-gradient(90deg, transparent 0 14px, rgba(74, 255, 138, 0.28) 14px 18px, transparent 18px 30px), linear-gradient(180deg, rgba(44, 126, 77, 0.84), rgba(4, 24, 24, 0.94));
  clip-path: polygon(0 32%, 8% 26%, 15% 36%, 24% 24%, 33% 35%, 44% 25%, 54% 38%, 66% 27%, 77% 36%, 89% 23%, 100% 34%, 100% 100%, 0 100%);
}

.planet-theme-forest .prop-a {
  left: 13%;
  bottom: 23%;
  width: 20px;
  height: 96px;
  background: #4aff8a;
  box-shadow: 24px -22px 0 10px rgba(74, 255, 138, 0.34), 52px 20px 0 -2px #75f4df, 92px -14px 0 8px rgba(74, 255, 138, 0.3), 680px 8px 0 -2px #75f4df, 724px -28px 0 10px rgba(74, 255, 138, 0.28);
}

.planet-theme-forest .prop-b {
  left: 32%;
  bottom: 30%;
  width: 72px;
  height: 72px;
  background: rgba(74, 255, 138, 0.34);
  clip-path: polygon(50% 0, 100% 36%, 82% 100%, 18% 100%, 0 36%);
  box-shadow: 188px 20px rgba(117, 244, 223, 0.28), 372px -18px rgba(74, 255, 138, 0.26);
}

.planet-theme-forest .prop-c {
  left: 18%;
  top: 24%;
  width: 5px;
  height: 5px;
  background: #ffd966;
  box-shadow: 64px 42px #4aff8a, 172px 6px #ffd966, 318px 72px #75f4df, 488px 28px #ffd966, 710px 82px #4aff8a, 880px 30px #75f4df;
  animation: twinkleB 2s steps(2) infinite;
}

.planet-theme-alien {
  --planet-accent: #ff7ea8;
  --planet-accent-glow: rgba(255, 126, 168, 0.5);
  background: radial-gradient(circle at 20% 16%, rgba(255, 126, 168, 0.16) 0 10%, transparent 11%), linear-gradient(rgba(255, 126, 168, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 126, 168, 0.04) 1px, transparent 1px), linear-gradient(180deg, #11071e, #24113c 70%, #12071d);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-alien .layer-sky {
  inset: 0;
  background: radial-gradient(circle at 70% 28%, rgba(117, 244, 223, 0.18) 0 5%, transparent 6%), repeating-linear-gradient(135deg, transparent 0 62px, rgba(255, 126, 168, 0.12) 62px 70px, transparent 70px 126px);
}

.planet-theme-alien .layer-back {
  left: 10%;
  bottom: 30%;
  width: 72%;
  height: 24%;
  background: rgba(168, 137, 255, 0.22);
  clip-path: polygon(0 88%, 9% 46%, 16% 70%, 24% 20%, 31% 74%, 39% 34%, 48% 80%, 58% 18%, 65% 72%, 76% 42%, 86% 76%, 100% 36%, 100% 100%, 0 100%);
}

.planet-theme-alien .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 36%;
  background: radial-gradient(circle at 24% 34%, rgba(255, 126, 168, 0.2) 0 6%, transparent 7%), radial-gradient(circle at 72% 28%, rgba(117, 244, 223, 0.14) 0 9%, transparent 10%), linear-gradient(180deg, rgba(68, 38, 102, 0.86), rgba(18, 7, 29, 0.94));
  clip-path: polygon(0 42%, 10% 30%, 21% 46%, 32% 28%, 45% 43%, 57% 28%, 70% 47%, 84% 31%, 100% 44%, 100% 100%, 0 100%);
}

.planet-theme-alien .prop-a {
  left: 18%;
  bottom: 27%;
  width: 76px;
  height: 44px;
  background: rgba(117, 244, 223, 0.5);
  clip-path: polygon(0 100%, 15% 35%, 50% 0, 85% 35%, 100% 100%);
  box-shadow: 104px 16px 0 -14px rgba(255, 126, 168, 0.62), 216px -12px 0 -6px rgba(117, 244, 223, 0.36), 612px 6px 0 -10px rgba(255, 126, 168, 0.5);
}

.planet-theme-alien .prop-b {
  right: 20%;
  bottom: 25%;
  width: 18px;
  height: 110px;
  background: #ff7ea8;
  box-shadow: -36px 22px #a889ff, 38px 18px #75f4df;
}

.planet-theme-alien .prop-b::before {
  position: absolute;
  top: -20px;
  left: -16px;
  width: 50px;
  height: 22px;
  background: rgba(255, 126, 168, 0.5);
  content: "";
}

.planet-theme-alien .prop-c {
  left: 21%;
  top: 27%;
  width: 8px;
  height: 8px;
  background: #75f4df;
  box-shadow: 78px 42px #ff7ea8, 208px 12px #75f4df, 390px 68px #a889ff, 566px 28px #ff7ea8, 754px 80px #75f4df;
  animation: alienSignal 1.6s steps(2) infinite;
}

.space-view .layer-atmosphere,
.space-view .layer-sky,
.space-view .layer-horizon,
.space-view .layer-back,
.space-view .layer-ground,
.space-view .layer-front,
.space-view .scene-prop {
  inset: auto;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  filter: none;
  opacity: 1;
  transform: none;
}

.space-view .prop-b,
.space-view .prop-c,
.space-view .prop-g {
  display: none;
}

.planet-theme-ice {
  --planet-accent: #a6efff;
  --planet-accent-glow: rgba(166, 239, 255, 0.5);
  background: radial-gradient(circle at 70% 22%, rgba(223, 246, 255, 0.32) 0 7%, transparent 8%), linear-gradient(rgba(166, 239, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(166, 239, 255, 0.04) 1px, transparent 1px), linear-gradient(180deg, #03101f 0%, #08213a 54%, #06131f 100%);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-ice .layer-atmosphere {
  inset: 0;
  background: linear-gradient(115deg, transparent 0 36%, rgba(223, 246, 255, 0.2) 36% 40%, transparent 40% 100%), linear-gradient(128deg, transparent 0 48%, rgba(117, 244, 223, 0.14) 48% 52%, transparent 52% 100%);
}

.planet-theme-ice .layer-sky {
  inset: 0 0 auto;
  height: 36%;
  background: linear-gradient(90deg, transparent 0 6%, rgba(223, 246, 255, 0.14) 6% 12%, transparent 12% 22%, rgba(166, 239, 255, 0.12) 22% 29%, transparent 29% 100%), radial-gradient(circle at 28% 60%, rgba(223, 246, 255, 0.1) 0 7%, transparent 8%);
}

.planet-theme-ice .layer-horizon {
  right: 5%;
  bottom: 27%;
  left: 4%;
  height: 46%;
  background: linear-gradient(180deg, rgba(223, 246, 255, 0.5), rgba(82, 161, 199, 0.22));
  clip-path: polygon(0 78%, 6% 48%, 11% 64%, 16% 20%, 22% 68%, 28% 34%, 34% 74%, 41% 16%, 48% 70%, 55% 35%, 63% 76%, 72% 24%, 80% 68%, 89% 40%, 100% 78%, 100% 100%, 0 100%);
}

.planet-theme-ice .layer-back {
  top: 0;
  right: 0;
  left: 0;
  height: 31%;
  background: #020a14;
  clip-path: polygon(0 0, 100% 0, 100% 30%, 95% 30%, 93% 60%, 90% 24%, 87% 24%, 85% 52%, 82% 22%, 77% 22%, 75% 66%, 70% 28%, 64% 28%, 61% 54%, 58% 18%, 52% 18%, 50% 70%, 45% 28%, 38% 28%, 36% 56%, 32% 22%, 25% 22%, 23% 62%, 18% 26%, 12% 26%, 10% 54%, 6% 32%, 0 34%);
  opacity: 0.86;
}

.planet-theme-ice .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 34%;
  background: repeating-linear-gradient(90deg, transparent 0 12px, rgba(223, 246, 255, 0.18) 12px 16px, transparent 16px 44px), linear-gradient(180deg, rgba(166, 239, 255, 0.66), rgba(31, 78, 118, 0.88));
  clip-path: polygon(0 34%, 9% 27%, 17% 38%, 25% 22%, 35% 34%, 44% 24%, 54% 37%, 64% 26%, 75% 40%, 85% 25%, 100% 36%, 100% 100%, 0 100%);
}

.planet-theme-ice .layer-front {
  right: 0;
  bottom: -2%;
  left: 0;
  height: 18%;
  background: #020914;
  clip-path: polygon(0 52%, 8% 38%, 15% 70%, 24% 36%, 36% 56%, 48% 34%, 60% 58%, 72% 32%, 86% 60%, 100% 42%, 100% 100%, 0 100%);
  opacity: 0.74;
}

.planet-theme-ice .prop-a {
  left: 11%;
  bottom: 21%;
  width: 18px;
  height: 104px;
  background: #dff6ff;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  box-shadow: 36px 22px 0 -2px rgba(166, 239, 255, 0.9), 86px 38px 0 -7px rgba(223, 246, 255, 0.78), 746px 26px 0 -4px rgba(166, 239, 255, 0.74);
}

.planet-theme-ice .prop-e {
  right: 14%;
  bottom: 25%;
  width: 30px;
  height: 84px;
  background: #75f4df;
  clip-path: polygon(50% 0, 100% 82%, 70% 100%, 30% 100%, 0 82%);
  box-shadow: -46px 26px 0 -6px #a6efff, 48px 34px 0 -10px #dff6ff, -690px 30px 0 -8px rgba(166, 239, 255, 0.9);
}

.planet-theme-ice .prop-f {
  left: 16%;
  top: 13%;
  width: 4px;
  height: 4px;
  background: rgba(223, 246, 255, 0.82);
  box-shadow: 108px 42px rgba(223, 246, 255, 0.76), 226px 12px rgba(166, 239, 255, 0.66), 386px 70px rgba(223, 246, 255, 0.68), 582px 36px rgba(166, 239, 255, 0.64), 764px 82px rgba(223, 246, 255, 0.62), 928px 28px rgba(166, 239, 255, 0.58);
  animation: snowFall 5s steps(6) infinite;
}

.planet-theme-lava {
  --planet-accent: #ff9c3f;
  --planet-accent-glow: rgba(255, 156, 63, 0.5);
  background: radial-gradient(circle at 62% 42%, rgba(255, 217, 102, 0.34) 0 10%, transparent 11%), linear-gradient(rgba(255, 107, 107, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 107, 107, 0.045) 1px, transparent 1px), linear-gradient(180deg, #27060b 0%, #66150f 42%, #210407 100%);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-lava .layer-atmosphere {
  inset: 0;
  background: repeating-linear-gradient(180deg, transparent 0 30px, rgba(255, 180, 95, 0.08) 30px 34px, transparent 34px 76px), radial-gradient(circle at 28% 72%, rgba(255, 156, 63, 0.18) 0 8%, transparent 9%);
}

.planet-theme-lava .layer-sky {
  top: 0;
  right: 0;
  left: 0;
  height: 29%;
  background: #4a070b;
  clip-path: polygon(0 0, 100% 0, 100% 62%, 96% 62%, 94% 78%, 91% 52%, 86% 52%, 84% 84%, 78% 54%, 73% 54%, 70% 76%, 63% 46%, 55% 46%, 52% 84%, 46% 54%, 39% 54%, 36% 78%, 29% 50%, 22% 50%, 20% 82%, 14% 56%, 8% 56%, 5% 76%, 0 62%);
  opacity: 0.68;
}

.planet-theme-lava .layer-horizon {
  right: 0;
  bottom: 28%;
  left: 0;
  height: 40%;
  background: linear-gradient(180deg, rgba(255, 120, 48, 0.58), rgba(96, 20, 18, 0.78)), repeating-linear-gradient(90deg, transparent 0 42px, rgba(255, 217, 102, 0.12) 42px 48px, transparent 48px 86px);
  clip-path: polygon(0 74%, 4% 58%, 9% 64%, 14% 36%, 21% 68%, 27% 28%, 34% 70%, 41% 42%, 49% 76%, 56% 34%, 64% 72%, 72% 30%, 80% 66%, 89% 42%, 100% 70%, 100% 100%, 0 100%);
}

.planet-theme-lava .layer-back {
  left: 6%;
  bottom: 21%;
  width: 42%;
  height: 48%;
  background: linear-gradient(180deg, #6b201d 0%, #351012 76%);
  clip-path: polygon(52% 0, 65% 19%, 74% 18%, 100% 100%, 0 100%, 26% 22%, 38% 21%);
  box-shadow: 230px 60px 0 -66px rgba(91, 27, 23, 0.9), 520px 88px 0 -84px rgba(86, 20, 22, 0.88);
}

.planet-theme-lava .layer-back::before {
  top: 18px;
  left: 44%;
  width: 86px;
  height: 34px;
  background: #ff9c3f;
  clip-path: polygon(0 100%, 20% 35%, 68% 35%, 100% 100%);
  box-shadow: 10px 20px 0 rgba(255, 107, 107, 0.72), 28px 38px 0 rgba(255, 217, 102, 0.45);
}

.planet-theme-lava .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 33%;
  background: repeating-linear-gradient(90deg, transparent 0 26px, rgba(255, 156, 63, 0.28) 26px 31px, transparent 31px 56px), linear-gradient(180deg, rgba(126, 34, 26, 0.9), rgba(26, 4, 8, 0.98));
  clip-path: polygon(0 42%, 11% 30%, 21% 45%, 33% 28%, 45% 44%, 58% 25%, 70% 42%, 83% 30%, 100% 45%, 100% 100%, 0 100%);
}

.planet-theme-lava .layer-front {
  right: 0;
  bottom: -1%;
  left: 0;
  height: 18%;
  background: #150204;
  clip-path: polygon(0 60%, 8% 48%, 16% 74%, 27% 38%, 40% 70%, 51% 44%, 63% 76%, 76% 36%, 88% 68%, 100% 50%, 100% 100%, 0 100%);
}

.planet-theme-lava .prop-a {
  left: 37%;
  bottom: 8%;
  width: 170px;
  height: 26px;
  background: #ff9c3f;
  box-shadow: -78px -18px 0 -8px #ff6b3f, 92px 16px 0 -4px #ffd966, 224px -22px 0 -10px #ff6b3f, -346px 20px 0 -6px #ffd966;
  transform: rotate(-38deg);
  filter: drop-shadow(0 0 12px rgba(255, 156, 63, 0.5));
}

.planet-theme-lava .prop-e {
  right: 13%;
  bottom: 24%;
  width: 16px;
  height: 96px;
  background: #2b0709;
  box-shadow: 28px 18px 0 -2px #3a0b0d, 68px 34px 0 2px #1b0507, 104px 10px 0 -4px #2b0709, -760px 28px 0 -2px #3a0b0d;
}

.planet-theme-lava .prop-f {
  left: 18%;
  top: 18%;
  width: 5px;
  height: 5px;
  background: #ffd966;
  box-shadow: 80px 42px #ff9c3f, 182px 12px #ff6b6b, 334px 72px #ffd966, 504px 28px #ff9c3f, 722px 86px #ff6b6b, 914px 34px #ffd966;
  animation: lavaPulse 1.2s steps(2) infinite;
}

.planet-theme-gas {
  --planet-accent: #a889ff;
  --planet-accent-glow: rgba(168, 137, 255, 0.5);
  background: radial-gradient(circle at 65% 24%, rgba(255, 126, 168, 0.18) 0 10%, transparent 11%), linear-gradient(rgba(168, 137, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(168, 137, 255, 0.04) 1px, transparent 1px), linear-gradient(180deg, #0b0824, #182049);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-gas .layer-atmosphere {
  inset: 0;
  background: repeating-linear-gradient(-7deg, transparent 0 36px, rgba(117, 244, 223, 0.15) 36px 54px, transparent 54px 94px), repeating-linear-gradient(8deg, transparent 0 58px, rgba(168, 137, 255, 0.14) 58px 80px, transparent 80px 132px);
  animation: gasFloat 8s steps(8) infinite;
}

.planet-theme-gas .layer-sky {
  left: 12%;
  top: 14%;
  width: 78%;
  height: 50%;
  background: repeating-linear-gradient(180deg, rgba(168, 137, 255, 0.22) 0 18px, rgba(117, 244, 223, 0.2) 18px 30px, rgba(77, 62, 147, 0.2) 30px 54px);
  clip-path: polygon(0 30%, 11% 18%, 24% 30%, 38% 16%, 52% 30%, 66% 14%, 82% 28%, 100% 16%, 100% 74%, 90% 86%, 76% 72%, 62% 88%, 47% 72%, 33% 86%, 17% 72%, 0 84%);
  opacity: 0.74;
}

.planet-theme-gas .layer-horizon {
  right: 8%;
  bottom: 24%;
  width: 290px;
  height: 132px;
  border: 6px solid rgba(117, 244, 223, 0.3);
  border-left-color: transparent;
  border-right-color: transparent;
  border-radius: 50%;
  transform: rotate(-12deg);
}

.planet-theme-gas .layer-back {
  left: 10%;
  bottom: 31%;
  width: 160px;
  height: 54px;
  background: rgba(117, 244, 223, 0.32);
  clip-path: polygon(0 66%, 12% 38%, 28% 52%, 42% 22%, 58% 54%, 76% 28%, 100% 64%, 100% 100%, 0 100%);
  box-shadow: 410px -46px 0 -16px rgba(255, 126, 168, 0.24), 518px 20px 0 -24px rgba(168, 137, 255, 0.32), 700px -8px 0 -30px rgba(117, 244, 223, 0.26);
}

.planet-theme-gas .layer-ground {
  right: -4%;
  bottom: -1%;
  left: -4%;
  height: 30%;
  background: linear-gradient(180deg, rgba(168, 137, 255, 0.02), rgba(14, 15, 54, 0.82)), repeating-linear-gradient(90deg, rgba(117, 244, 223, 0.14) 0 24px, transparent 24px 70px);
  clip-path: polygon(0 52%, 9% 40%, 20% 48%, 32% 36%, 45% 46%, 59% 34%, 74% 48%, 88% 38%, 100% 50%, 100% 100%, 0 100%);
}

.planet-theme-gas .layer-front {
  right: 0;
  bottom: 0;
  left: 0;
  height: 12%;
  background: rgba(5, 6, 24, 0.68);
  clip-path: polygon(0 56%, 14% 38%, 28% 60%, 43% 34%, 58% 62%, 72% 40%, 86% 58%, 100% 36%, 100% 100%, 0 100%);
}

.planet-theme-gas .prop-a {
  left: 16%;
  bottom: 42%;
  width: 120px;
  height: 24px;
  background: rgba(117, 244, 223, 0.36);
  box-shadow: 18px 18px rgba(168, 137, 255, 0.26), 384px -42px rgba(117, 244, 223, 0.24), 426px -22px rgba(255, 126, 168, 0.2), 644px 28px rgba(168, 137, 255, 0.24);
  animation: gasFloat 6s steps(8) infinite;
}

.planet-theme-gas .prop-e {
  left: 44%;
  top: 34%;
  width: 122px;
  height: 58px;
  border: 6px solid rgba(255, 126, 168, 0.52);
  border-left-color: rgba(117, 244, 223, 0.42);
  border-radius: 50%;
  box-shadow: inset 22px 0 0 rgba(168, 137, 255, 0.18);
}

.planet-theme-gas .prop-f {
  right: 23%;
  bottom: 31%;
  width: 18px;
  height: 72px;
  background: rgba(117, 244, 223, 0.42);
  box-shadow: -28px 22px rgba(168, 137, 255, 0.34), 32px 16px rgba(255, 126, 168, 0.28), -620px 36px rgba(117, 244, 223, 0.26);
}

.planet-theme-desert {
  --planet-accent: #ffca7a;
  --planet-accent-glow: rgba(255, 202, 122, 0.46);
  background: radial-gradient(circle at 18% 58%, rgba(255, 249, 179, 0.46) 0 11%, transparent 12%), linear-gradient(rgba(255, 202, 122, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 202, 122, 0.04) 1px, transparent 1px), linear-gradient(180deg, #2f160a 0%, #bc5b1f 48%, #4f2814 100%);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-desert .layer-atmosphere {
  inset: 0;
  background: repeating-linear-gradient(0deg, transparent 0 46px, rgba(255, 217, 102, 0.1) 46px 50px, transparent 50px 84px), linear-gradient(172deg, transparent 0 40%, rgba(255, 202, 122, 0.1) 40% 43%, transparent 43% 100%);
}

.planet-theme-desert .layer-sky {
  top: 7%;
  right: 6%;
  left: 7%;
  height: 26%;
  background: rgba(255, 217, 102, 0.22);
  clip-path: polygon(0 42%, 5% 42%, 5% 36%, 16% 36%, 16% 30%, 29% 30%, 29% 38%, 40% 38%, 40% 44%, 56% 44%, 56% 34%, 67% 34%, 67% 42%, 83% 42%, 83% 36%, 100% 36%, 100% 48%, 0 48%);
  box-shadow: -150px 70px 0 -8px rgba(255, 217, 102, 0.18), 460px 92px 0 -12px rgba(255, 217, 102, 0.14);
}

.planet-theme-desert .layer-horizon {
  right: 0;
  bottom: 24%;
  left: 0;
  height: 45%;
  background: linear-gradient(180deg, rgba(255, 157, 78, 0.74), rgba(112, 57, 28, 0.54));
  clip-path: polygon(0 78%, 5% 56%, 10% 70%, 15% 42%, 23% 74%, 29% 40%, 38% 68%, 45% 34%, 53% 72%, 61% 44%, 67% 76%, 74% 54%, 82% 74%, 91% 38%, 100% 62%, 100% 100%, 0 100%);
}

.planet-theme-desert .layer-back {
  left: 4%;
  bottom: 27%;
  width: 62%;
  height: 30%;
  background: #9b4d27;
  clip-path: polygon(0 82%, 4% 44%, 11% 44%, 15% 68%, 21% 68%, 25% 26%, 34% 26%, 40% 74%, 49% 74%, 54% 38%, 64% 38%, 69% 78%, 79% 78%, 83% 58%, 92% 58%, 100% 82%, 100% 100%, 0 100%);
  opacity: 0.76;
}

.planet-theme-desert .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 38%;
  background: repeating-linear-gradient(90deg, rgba(255, 202, 122, 0.16) 0 10px, transparent 10px 24px), repeating-linear-gradient(172deg, transparent 0 34px, rgba(255, 217, 102, 0.14) 34px 40px, transparent 40px 74px), linear-gradient(180deg, rgba(216, 112, 48, 0.82), rgba(76, 39, 18, 0.94));
  clip-path: polygon(0 36%, 13% 48%, 31% 32%, 49% 44%, 68% 29%, 85% 42%, 100% 30%, 100% 100%, 0 100%);
}

.planet-theme-desert .layer-front {
  right: 0;
  bottom: 0;
  left: 0;
  height: 20%;
  background: #20130b;
  clip-path: polygon(0 64%, 8% 52%, 12% 72%, 21% 48%, 28% 70%, 39% 52%, 49% 78%, 61% 46%, 72% 70%, 83% 48%, 100% 66%, 100% 100%, 0 100%);
}

.planet-theme-desert .prop-a {
  left: 16%;
  bottom: 26%;
  width: 92px;
  height: 110px;
  background: #d16f3a;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  box-shadow: 128px 28px 0 -22px #c76032, 180px 54px 0 -34px rgba(255, 202, 122, 0.72), 650px 10px 0 -20px #a34c28;
}

.planet-theme-desert .prop-e {
  right: 20%;
  bottom: 17%;
  width: 18px;
  height: 106px;
  background: #211610;
  box-shadow: -18px 34px 0 -5px #211610, 18px 22px 0 -5px #211610, -710px 32px 0 -3px #211610, -688px 48px 0 -8px #211610, -336px 70px 0 -8px #211610;
}

.planet-theme-desert .prop-f {
  left: 9%;
  bottom: 18%;
  width: 180px;
  height: 8px;
  background: rgba(255, 217, 102, 0.3);
  box-shadow: 320px 34px rgba(255, 202, 122, 0.24), 594px 8px rgba(255, 217, 102, 0.2);
  animation: dustDrift 8s steps(8) infinite;
}

.planet-theme-ocean {
  --planet-accent: #75f4df;
  --planet-accent-glow: rgba(117, 244, 223, 0.48);
  background: radial-gradient(circle at 28% 28%, rgba(166, 239, 255, 0.28) 0 13%, transparent 14%), linear-gradient(rgba(40, 127, 214, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(40, 127, 214, 0.05) 1px, transparent 1px), linear-gradient(180deg, #031021, #064469 58%, #031626 100%);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-ocean .layer-atmosphere {
  inset: 0;
  background: repeating-linear-gradient(180deg, transparent 0 22px, rgba(117, 244, 223, 0.09) 22px 26px, transparent 26px 52px), linear-gradient(115deg, transparent 0 42%, rgba(166, 239, 255, 0.12) 42% 46%, transparent 46% 100%);
}

.planet-theme-ocean .layer-sky {
  top: 0;
  right: 0;
  left: 0;
  height: 27%;
  background: #010916;
  clip-path: polygon(0 0, 100% 0, 100% 52%, 95% 52%, 93% 80%, 88% 54%, 82% 54%, 78% 86%, 72% 58%, 64% 58%, 61% 82%, 54% 50%, 47% 50%, 44% 78%, 36% 56%, 30% 56%, 27% 86%, 20% 52%, 13% 52%, 10% 80%, 5% 58%, 0 58%);
  opacity: 0.76;
}

.planet-theme-ocean .layer-horizon {
  right: 0;
  bottom: 31%;
  left: 0;
  height: 18%;
  background: repeating-linear-gradient(90deg, rgba(117, 244, 223, 0.34) 0 34px, rgba(40, 127, 214, 0.12) 34px 82px);
  clip-path: polygon(0 46%, 9% 60%, 19% 40%, 31% 58%, 43% 42%, 56% 60%, 70% 38%, 84% 58%, 100% 42%, 100% 100%, 0 100%);
  animation: oceanWave 5s steps(6) infinite;
}

.planet-theme-ocean .layer-back {
  right: 7%;
  bottom: 25%;
  left: 6%;
  height: 35%;
  background: rgba(9, 58, 90, 0.72);
  clip-path: polygon(0 86%, 5% 48%, 11% 74%, 16% 38%, 22% 72%, 29% 52%, 37% 82%, 44% 34%, 51% 78%, 60% 48%, 70% 82%, 79% 40%, 88% 74%, 100% 52%, 100% 100%, 0 100%);
}

.planet-theme-ocean .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 35%;
  background: repeating-linear-gradient(180deg, rgba(117, 244, 223, 0.16) 0 8px, transparent 8px 24px), linear-gradient(180deg, rgba(40, 127, 214, 0.68), rgba(3, 22, 38, 0.92));
  clip-path: polygon(0 22%, 12% 34%, 25% 22%, 38% 36%, 52% 24%, 66% 34%, 81% 20%, 100% 31%, 100% 100%, 0 100%);
}

.planet-theme-ocean .layer-front {
  right: 0;
  bottom: -1%;
  left: 0;
  height: 20%;
  background: #020b16;
  clip-path: polygon(0 64%, 7% 42%, 14% 78%, 23% 46%, 31% 70%, 43% 40%, 51% 72%, 65% 48%, 72% 78%, 84% 42%, 100% 60%, 100% 100%, 0 100%);
}

.planet-theme-ocean .prop-a {
  left: 17%;
  bottom: 28%;
  width: 150px;
  height: 54px;
  background: #184838;
  clip-path: polygon(0 82%, 18% 28%, 48% 10%, 78% 32%, 100% 82%, 100% 100%, 0 100%);
  box-shadow: 30px -22px 0 -16px #4aff8a, 680px 0 0 -38px #244c3f;
}

.planet-theme-ocean .prop-e {
  right: 16%;
  bottom: 18%;
  width: 18px;
  height: 74px;
  background: #ff7ea8;
  clip-path: polygon(50% 0, 100% 68%, 62% 100%, 38% 100%, 0 68%);
  box-shadow: -34px 18px 0 -4px #75f4df, 34px 16px 0 -5px #ffca7a, -706px 14px 0 -6px #ff7ea8, -650px 34px 0 -12px #75f4df;
}

.planet-theme-ocean .prop-f {
  right: 17%;
  bottom: 18%;
  width: 10px;
  height: 10px;
  background: rgba(117, 244, 223, 0.62);
  box-shadow: -30px -44px rgba(117, 244, 223, 0.46), 24px -76px rgba(126, 207, 255, 0.42), 72px -28px rgba(117, 244, 223, 0.4), 118px -92px rgba(126, 207, 255, 0.34);
  animation: bubbleRise 4.5s steps(8) infinite;
}

.planet-theme-forest {
  --planet-accent: #4aff8a;
  --planet-accent-glow: rgba(74, 255, 138, 0.46);
  background: radial-gradient(circle at 74% 18%, rgba(255, 217, 102, 0.14) 0 8%, transparent 9%), linear-gradient(rgba(74, 255, 138, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(74, 255, 138, 0.04) 1px, transparent 1px), linear-gradient(180deg, #03151a, #0a2a23 64%, #031313);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-forest .layer-atmosphere {
  inset: 0;
  background: radial-gradient(circle at 8% 22%, rgba(74, 255, 138, 0.15) 0 7%, transparent 8%), radial-gradient(circle at 24% 30%, rgba(117, 244, 223, 0.12) 0 8%, transparent 9%), radial-gradient(circle at 58% 24%, rgba(74, 255, 138, 0.12) 0 9%, transparent 10%);
}

.planet-theme-forest .layer-sky {
  top: 0;
  right: 0;
  left: 0;
  height: 27%;
  background: #021214;
  clip-path: polygon(0 0, 100% 0, 100% 60%, 94% 46%, 88% 68%, 81% 42%, 74% 70%, 66% 40%, 57% 66%, 49% 42%, 40% 70%, 32% 44%, 24% 68%, 16% 46%, 8% 70%, 0 54%);
  opacity: 0.68;
}

.planet-theme-forest .layer-horizon {
  right: 0;
  bottom: 29%;
  left: 0;
  height: 39%;
  background: repeating-linear-gradient(90deg, rgba(44, 126, 77, 0.56) 0 18px, transparent 18px 52px), linear-gradient(180deg, rgba(74, 255, 138, 0.18), rgba(5, 38, 30, 0.74));
  clip-path: polygon(0 74%, 6% 30%, 13% 74%, 22% 26%, 32% 78%, 42% 34%, 51% 76%, 63% 22%, 74% 78%, 84% 32%, 94% 76%, 100% 48%, 100% 100%, 0 100%);
}

.planet-theme-forest .layer-back {
  right: 0;
  bottom: 22%;
  left: 0;
  height: 36%;
  background: rgba(11, 79, 51, 0.5);
  clip-path: polygon(0 86%, 5% 54%, 11% 80%, 18% 46%, 24% 84%, 31% 42%, 40% 78%, 48% 48%, 57% 84%, 65% 40%, 73% 80%, 82% 48%, 91% 82%, 100% 44%, 100% 100%, 0 100%);
}

.planet-theme-forest .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 36%;
  background: repeating-linear-gradient(90deg, transparent 0 14px, rgba(74, 255, 138, 0.28) 14px 18px, transparent 18px 30px), linear-gradient(180deg, rgba(44, 126, 77, 0.84), rgba(4, 24, 24, 0.94));
  clip-path: polygon(0 32%, 8% 26%, 15% 36%, 24% 24%, 33% 35%, 44% 25%, 54% 38%, 66% 27%, 77% 36%, 89% 23%, 100% 34%, 100% 100%, 0 100%);
}

.planet-theme-forest .layer-front {
  right: 0;
  bottom: 0;
  left: 0;
  height: 21%;
  background: #031014;
  clip-path: polygon(0 70%, 6% 42%, 14% 76%, 22% 38%, 30% 72%, 41% 44%, 52% 78%, 62% 40%, 72% 76%, 83% 42%, 92% 74%, 100% 48%, 100% 100%, 0 100%);
}

.planet-theme-forest .prop-a {
  left: 12%;
  bottom: 22%;
  width: 18px;
  height: 116px;
  background: #163b2d;
  box-shadow: 28px -22px 0 12px rgba(74, 255, 138, 0.34), 56px 18px 0 -2px #75f4df, 98px -16px 0 10px rgba(74, 255, 138, 0.3), 680px 8px 0 -2px #75f4df, 724px -28px 0 10px rgba(74, 255, 138, 0.28);
}

.planet-theme-forest .prop-e {
  left: 31%;
  bottom: 29%;
  width: 72px;
  height: 72px;
  background: rgba(74, 255, 138, 0.34);
  clip-path: polygon(50% 0, 100% 36%, 82% 100%, 18% 100%, 0 36%);
  box-shadow: 188px 20px rgba(117, 244, 223, 0.28), 372px -18px rgba(74, 255, 138, 0.26);
}

.planet-theme-forest .prop-f {
  left: 18%;
  top: 24%;
  width: 5px;
  height: 5px;
  background: #ffd966;
  box-shadow: 64px 42px #4aff8a, 172px 6px #ffd966, 318px 72px #75f4df, 488px 28px #ffd966, 710px 82px #4aff8a, 880px 30px #75f4df;
  animation: twinkleB 2s steps(2) infinite;
}

.planet-theme-alien {
  --planet-accent: #ff7ea8;
  --planet-accent-glow: rgba(255, 126, 168, 0.5);
  background: radial-gradient(circle at 24% 18%, rgba(255, 126, 168, 0.18) 0 9%, transparent 10%), linear-gradient(rgba(255, 126, 168, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 126, 168, 0.04) 1px, transparent 1px), linear-gradient(180deg, #11071e, #24113c 70%, #12071d);
  background-size: 100% 100%, 28px 28px, 28px 28px, 100% 100%;
}

.planet-theme-alien .layer-atmosphere {
  inset: 0;
  background: radial-gradient(circle at 70% 28%, rgba(117, 244, 223, 0.18) 0 5%, transparent 6%), repeating-linear-gradient(135deg, transparent 0 62px, rgba(255, 126, 168, 0.12) 62px 70px, transparent 70px 126px);
}

.planet-theme-alien .layer-sky {
  top: 0;
  right: 0;
  left: 0;
  height: 27%;
  background: #060210;
  clip-path: polygon(0 0, 100% 0, 100% 52%, 94% 40%, 90% 72%, 84% 42%, 76% 70%, 69% 38%, 61% 78%, 54% 42%, 46% 72%, 37% 40%, 29% 78%, 21% 44%, 13% 70%, 6% 46%, 0 60%);
  opacity: 0.68;
}

.planet-theme-alien .layer-horizon {
  left: 18%;
  bottom: 28%;
  width: 58%;
  height: 45%;
  background: rgba(64, 20, 62, 0.72);
  clip-path: polygon(0 100%, 5% 70%, 17% 60%, 24% 32%, 36% 20%, 47% 34%, 52% 8%, 58% 34%, 70% 22%, 82% 52%, 96% 60%, 100% 100%);
}

.planet-theme-alien .layer-horizon::before {
  left: 46%;
  top: 32%;
  width: 6px;
  height: 6px;
  background: #dff6ff;
  box-shadow: 22px 0 #dff6ff;
  filter: drop-shadow(0 0 8px rgba(223, 246, 255, 0.8));
}

.planet-theme-alien .layer-back {
  left: 5%;
  bottom: 24%;
  width: 88%;
  height: 32%;
  background: rgba(168, 137, 255, 0.22);
  clip-path: polygon(0 88%, 9% 46%, 16% 70%, 24% 20%, 31% 74%, 39% 34%, 48% 80%, 58% 18%, 65% 72%, 76% 42%, 86% 76%, 100% 36%, 100% 100%, 0 100%);
}

.planet-theme-alien .layer-ground {
  right: 0;
  bottom: 0;
  left: 0;
  height: 36%;
  background: radial-gradient(circle at 24% 34%, rgba(255, 126, 168, 0.22) 0 6%, transparent 7%), radial-gradient(circle at 72% 28%, rgba(117, 244, 223, 0.16) 0 9%, transparent 10%), linear-gradient(180deg, rgba(68, 38, 102, 0.86), rgba(18, 7, 29, 0.94));
  clip-path: polygon(0 42%, 10% 30%, 21% 46%, 32% 28%, 45% 43%, 57% 28%, 70% 47%, 84% 31%, 100% 44%, 100% 100%, 0 100%);
}

.planet-theme-alien .layer-front {
  right: 0;
  bottom: 0;
  left: 0;
  height: 21%;
  background: #070211;
  clip-path: polygon(0 68%, 5% 36%, 14% 82%, 22% 42%, 31% 72%, 42% 38%, 52% 78%, 64% 44%, 75% 82%, 84% 36%, 92% 70%, 100% 48%, 100% 100%, 0 100%);
}

.planet-theme-alien .prop-a {
  left: 17%;
  bottom: 27%;
  width: 78px;
  height: 46px;
  background: rgba(117, 244, 223, 0.5);
  clip-path: polygon(0 100%, 15% 35%, 50% 0, 85% 35%, 100% 100%);
  box-shadow: 104px 16px 0 -14px rgba(255, 126, 168, 0.62), 216px -12px 0 -6px rgba(117, 244, 223, 0.36), 612px 6px 0 -10px rgba(255, 126, 168, 0.5);
}

.planet-theme-alien .prop-b::before {
  top: -18px;
  left: -18px;
  width: 54px;
  height: 24px;
  background: rgba(255, 126, 168, 0.5);
  clip-path: polygon(0 100%, 18% 18%, 50% 0, 82% 18%, 100% 100%);
}

.planet-theme-alien .prop-e {
  right: 19%;
  bottom: 24%;
  width: 18px;
  height: 116px;
  background: #ff7ea8;
  box-shadow: -36px 22px #a889ff, 38px 18px #75f4df, -730px 42px 0 -2px #75f4df;
}

.planet-theme-alien .prop-f {
  left: 21%;
  top: 27%;
  width: 8px;
  height: 8px;
  background: #75f4df;
  box-shadow: 78px 42px #ff7ea8, 208px 12px #75f4df, 390px 68px #a889ff, 566px 28px #ff7ea8, 754px 80px #75f4df;
  animation: alienSignal 1.6s steps(2) infinite;
}

.planet-life-microbial .prop-d {
  display: block;
  left: 12%;
  bottom: 24%;
  width: 6px;
  height: 6px;
  background: #4aff8a;
  box-shadow: 34px 18px rgba(74, 255, 138, 0.72), 92px -14px rgba(117, 244, 223, 0.62), 178px 22px rgba(74, 255, 138, 0.54), 412px -6px rgba(117, 244, 223, 0.56), 628px 26px rgba(74, 255, 138, 0.48);
  filter: drop-shadow(0 0 6px rgba(74, 255, 138, 0.7));
}

.planet-life-intelligent .prop-d {
  display: block;
  right: 31%;
  bottom: 22%;
  width: 28px;
  height: 36px;
  background: #75f4df;
  box-shadow: -34px 10px #ff7ea8, 34px 12px #a889ff, 0 -18px rgba(117, 244, 223, 0.34);
  filter: drop-shadow(0 0 8px rgba(117, 244, 223, 0.56));
}

.planet-life-intelligent .prop-d::before {
  position: absolute;
  top: -16px;
  left: 7px;
  width: 14px;
  height: 14px;
  background: #dff6ff;
  box-shadow: -4px 4px #0d1525, 8px 4px #0d1525;
  content: "";
}

.planet-hazard-radiation .planet-scene::after {
  inset: 0;
  background: repeating-linear-gradient(90deg, transparent 0 44px, rgba(255, 217, 102, 0.08) 44px 48px, transparent 48px 92px);
  opacity: 0.64;
}

.planet-hazard-gravity_anomaly .planet-scene::after {
  left: 36%;
  top: 20%;
  width: 280px;
  height: 160px;
  border: 4px solid rgba(117, 244, 223, 0.22);
  border-left-color: transparent;
  border-right-color: transparent;
  border-radius: 50%;
  transform: rotate(-16deg);
  filter: drop-shadow(0 0 12px rgba(117, 244, 223, 0.18));
}

.planet-hazard-hostile_life .planet-scene::after {
  left: 9%;
  bottom: 18%;
  width: 10px;
  height: 10px;
  background: #ff6b6b;
  box-shadow: 28px 0 #ff6b6b, 210px 34px #ff6b6b, 238px 34px #ff6b6b, 638px -18px #ff6b6b, 666px -18px #ff6b6b;
  filter: drop-shadow(0 0 8px rgba(255, 107, 107, 0.55));
}

.space-view[class*=planet-theme-] .planet-scene::before {
  inset: 0;
  z-index: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  image-rendering: pixelated;
  opacity: 1;
}

.space-view[class*=planet-theme-] .planet-scene > .scene-layer,
.space-view[class*=planet-theme-] .planet-scene > .prop-a,
.space-view[class*=planet-theme-] .planet-scene > .prop-b,
.space-view[class*=planet-theme-] .planet-scene > .prop-c,
.space-view[class*=planet-theme-] .planet-scene > .prop-e,
.space-view[class*=planet-theme-] .planet-scene > .prop-f,
.space-view[class*=planet-theme-] .planet-scene > .prop-g {
  display: none;
}

.planet-theme-lava .planet-scene::before {
  background-image: url("/assets/planet-bg/lava.svg");
}

.planet-theme-desert .planet-scene::before {
  background-image: url("/assets/planet-bg/desert.svg");
}

.planet-theme-ice .planet-scene::before {
  background-image: url("/assets/planet-bg/ice.svg");
}

.planet-theme-ocean .planet-scene::before {
  background-image: url("/assets/planet-bg/ocean.svg");
}

.planet-theme-forest .planet-scene::before {
  background-image: url("/assets/planet-bg/forest.svg");
}

.planet-theme-alien .planet-scene::before {
  background-image: url("/assets/planet-bg/alien.svg");
}

.planet-theme-gas .planet-scene::before {
  background-image: url("/assets/planet-bg/gas.svg");
}

.space-stars,
.stars {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.stars {
  width: 2px;
  height: 2px;
  background: #f3fbff;
}

.stars-a {
  left: 28px;
  top: 22px;
  box-shadow: 58px 54px #7ecfff, 162px 18px #ffd966, 268px 92px #f3fbff, 410px 38px #7ecfff, 552px 124px #f3fbff, 710px 66px #ffd966, 860px 152px #7ecfff, 990px 44px #f3fbff, 1128px 106px #ffd966, 104px 236px #f3fbff, 318px 312px #7ecfff, 484px 228px #ffd966, 660px 366px #f3fbff, 822px 284px #7ecfff, 1036px 348px #f3fbff, 1210px 268px #ffd966, 76px 492px #7ecfff, 268px 566px #f3fbff, 520px 506px #ffd966, 752px 598px #7ecfff, 980px 512px #f3fbff, 1190px 570px #ffd966;
  animation: twinkleA 1.8s steps(2) infinite;
}

.stars-b {
  left: 0;
  top: 0;
  width: 3px;
  height: 3px;
  background: #4aff8a;
  opacity: 0.72;
  box-shadow: 126px 168px #4aff8a, 238px 76px #f3fbff, 386px 184px #ffd966, 582px 92px #7ecfff, 742px 208px #f3fbff, 918px 122px #4aff8a, 1098px 214px #ffd966, 82px 382px #f3fbff, 242px 428px #7ecfff, 448px 404px #4aff8a, 686px 470px #ffd966, 900px 418px #f3fbff, 1138px 462px #7ecfff;
  animation: twinkleB 2.4s steps(2) infinite;
}

.stars-c {
  left: 14%;
  top: 8%;
  width: 4px;
  height: 4px;
  background: rgba(126, 207, 255, 0.28);
  box-shadow: 12px 0 rgba(126, 207, 255, 0.2), 24px 8px rgba(255, 217, 102, 0.18), 42px 16px rgba(74, 255, 138, 0.18), 66px 24px rgba(126, 207, 255, 0.16), 720px 320px rgba(255, 217, 102, 0.16), 740px 332px rgba(126, 207, 255, 0.22), 768px 344px rgba(74, 255, 138, 0.14);
  opacity: 0.7;
  animation: pixelDrift 7s steps(8) infinite;
}

.planet-title {
  position: absolute;
  z-index: 4;
  left: 50%;
  top: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: calc(100% - 260px);
  transform: translateX(-50%);
}

.eyebrow {
  display: block;
  margin-bottom: 6px;
  color: #7ecfff;
  font-size: 8px;
}

.planet-title h2 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 16px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planet-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.badge {
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  border: 2px solid #1a2030;
  padding: 0 8px;
  background: #111d32;
  font-size: 9px;
  line-height: 1;
}

.grade-badge {
  border-color: #ffd966;
  color: #ffd966;
}

.info-badge {
  border-color: #7ecfff;
  color: #7ecfff;
}

.risk-badge {
  border-color: #4aff8a;
  color: #4aff8a;
}

.risk-badge.is-critical {
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.orbit-line {
  position: absolute;
  z-index: 1;
  left: 16%;
  top: 76%;
  width: 72%;
  height: 4px;
  background: repeating-linear-gradient(90deg, var(--planet-accent) 0 12px, transparent 12px 24px);
  opacity: 0.75;
  transform: rotate(-37deg);
  transform-origin: left center;
  filter: drop-shadow(0 0 8px var(--planet-accent-glow));
}

.planet-pixel {
  position: absolute;
  z-index: 3;
  right: 52px;
  top: 72px;
  width: 158px;
  aspect-ratio: 1;
  --planet-body: #536cc8;
  --planet-mid: #2d408a;
  --planet-dark: #16102e;
  --planet-light: #aeeeff;
  --planet-accent-surface: #75f4df;
  --planet-line: rgba(126, 207, 255, 0.16);
  --planet-ring: rgba(126, 207, 255, 0.72);
  --planet-ring-shadow: rgba(255, 126, 168, 0.28);
  --planet-glow: rgba(126, 207, 255, 0.34);
  overflow: visible;
  image-rendering: pixelated;
  filter: drop-shadow(10px 12px 0 rgba(3, 4, 14, 0.58)) drop-shadow(0 0 12px var(--planet-glow));
}

.planet-pixel::before,
.planet-pixel::after,
.planet-pixel span {
  position: absolute;
  display: block;
  border-radius: 0;
  content: "";
  image-rendering: pixelated;
}

.planet-pixel::before {
  z-index: 2;
  inset: 0;
  background: radial-gradient(circle at 30% 23%, rgba(255, 255, 255, 0.38) 0 6%, transparent 7%), linear-gradient(135deg, transparent 0 13%, rgba(255, 255, 255, 0.2) 13% 18%, transparent 18%), repeating-linear-gradient(156deg, transparent 0 18px, var(--planet-line) 18px 26px, transparent 26px 46px), linear-gradient(145deg, var(--planet-light) 0 9%, var(--planet-body) 9% 38%, var(--planet-mid) 38% 68%, var(--planet-dark) 68% 100%);
  clip-path: polygon(31.25% 0, 68.75% 0, 68.75% 6.25%, 81.25% 6.25%, 81.25% 12.5%, 87.5% 12.5%, 87.5% 25%, 93.75% 25%, 93.75% 31.25%, 100% 31.25%, 100% 68.75%, 93.75% 68.75%, 93.75% 75%, 87.5% 75%, 87.5% 87.5%, 81.25% 87.5%, 81.25% 93.75%, 68.75% 93.75%, 68.75% 100%, 31.25% 100%, 31.25% 93.75%, 18.75% 93.75%, 18.75% 87.5%, 12.5% 87.5%, 12.5% 75%, 6.25% 75%, 6.25% 68.75%, 0 68.75%, 0 31.25%, 6.25% 31.25%, 6.25% 25%, 12.5% 25%, 12.5% 12.5%, 18.75% 12.5%, 18.75% 6.25%, 31.25% 6.25%);
}

.planet-pixel::after {
  z-index: 1;
  left: -16%;
  top: 34%;
  width: 132%;
  height: 34%;
  background: linear-gradient(180deg, transparent 0 18%, var(--planet-ring) 18% 27%, transparent 27% 50%, var(--planet-ring-shadow) 50% 62%, transparent 62%), repeating-linear-gradient(90deg, transparent 0 18px, rgba(223, 246, 255, 0.25) 18px 26px, transparent 26px 46px);
  clip-path: polygon(0 48%, 10% 28%, 29% 12%, 50% 2%, 71% 12%, 90% 28%, 100% 48%, 100% 58%, 90% 78%, 71% 94%, 50% 100%, 29% 94%, 10% 78%, 0 58%);
  opacity: 0.58;
  transform: rotate(-15deg);
  filter: drop-shadow(0 0 9px var(--planet-glow));
}

.planet-pixel span:nth-child(1) {
  z-index: 3;
  left: 17%;
  top: 15%;
  width: 46%;
  height: 12%;
  background: linear-gradient(90deg, var(--planet-light), var(--planet-accent-surface));
  box-shadow: 10px 22px 0 -3px var(--planet-accent-surface), 32px 58px 0 -1px var(--planet-light), 74px 90px 0 -4px var(--planet-accent-surface);
  clip-path: polygon(0 0, 86% 0, 100% 48%, 78% 100%, 10% 100%, 10% 58%, 0 58%);
  opacity: 0.72;
}

.planet-pixel span:nth-child(2) {
  z-index: 3;
  right: 11%;
  top: 40%;
  width: 58%;
  height: 14%;
  background: linear-gradient(90deg, transparent 0 8%, var(--planet-dark) 8% 42%, rgba(8, 9, 26, 0.55) 42% 70%, transparent 70%), repeating-linear-gradient(90deg, transparent 0 11px, rgba(223, 246, 255, 0.13) 11px 16px, transparent 16px 28px);
  box-shadow: -72px 34px 0 -8px var(--planet-mid), -42px 70px 0 -8px var(--planet-dark), 4px 82px 0 -8px var(--planet-mid);
  clip-path: polygon(6% 12%, 100% 12%, 100% 70%, 72% 70%, 72% 100%, 14% 100%, 14% 56%, 0 56%);
  opacity: 0.76;
}

.planet-pixel span:nth-child(3) {
  z-index: 4;
  left: 42%;
  bottom: 17%;
  width: 15%;
  height: 22%;
  background: var(--planet-accent-surface);
  box-shadow: -42px -46px 0 -7px var(--planet-accent-surface), 46px -18px 0 -8px var(--planet-light), 38px 24px 0 -9px var(--planet-accent-surface);
  clip-path: polygon(50% 0, 100% 26%, 100% 74%, 70% 100%, 30% 100%, 0 74%, 0 26%);
  opacity: 0.74;
}

.type-lava {
  --planet-body: #9e3a47;
  --planet-mid: #672047;
  --planet-dark: #1d0a22;
  --planet-light: #ffbd78;
  --planet-accent-surface: #ff6b6b;
  --planet-line: rgba(255, 156, 63, 0.18);
  --planet-ring: rgba(255, 156, 63, 0.64);
  --planet-ring-shadow: rgba(255, 107, 107, 0.26);
  --planet-glow: rgba(255, 126, 168, 0.34);
}

.type-gas {
  --planet-body: #8a72d5;
  --planet-mid: #56429e;
  --planet-dark: #1a1236;
  --planet-light: #d5c4ff;
  --planet-accent-surface: #75f4df;
  --planet-line: rgba(117, 244, 223, 0.16);
  --planet-ring: rgba(223, 246, 255, 0.72);
  --planet-ring-shadow: rgba(168, 137, 255, 0.34);
  --planet-glow: rgba(168, 137, 255, 0.42);
}

.type-gas::after {
  opacity: 0.86;
}

.type-desert {
  --planet-body: #b17a63;
  --planet-mid: #6d4b56;
  --planet-dark: #201326;
  --planet-light: #ffd6a0;
  --planet-accent-surface: #ffca7a;
  --planet-line: rgba(255, 202, 122, 0.14);
  --planet-ring: rgba(255, 202, 122, 0.56);
  --planet-ring-shadow: rgba(255, 126, 168, 0.18);
  --planet-glow: rgba(255, 202, 122, 0.24);
}

.type-ocean {
  --planet-body: #3676be;
  --planet-mid: #244a95;
  --planet-dark: #111631;
  --planet-light: #a6efff;
  --planet-accent-surface: #4aff8a;
  --planet-line: rgba(117, 244, 223, 0.15);
  --planet-ring: rgba(117, 244, 223, 0.58);
  --planet-ring-shadow: rgba(126, 207, 255, 0.24);
  --planet-glow: rgba(117, 244, 223, 0.32);
}

.type-ice {
  --planet-body: #93bfd2;
  --planet-mid: #4f789e;
  --planet-dark: #111a32;
  --planet-light: #effcff;
  --planet-accent-surface: #a6efff;
  --planet-line: rgba(223, 246, 255, 0.18);
  --planet-ring: rgba(223, 246, 255, 0.62);
  --planet-ring-shadow: rgba(126, 207, 255, 0.2);
  --planet-glow: rgba(166, 239, 255, 0.34);
}

.type-forest {
  --planet-body: #3d8a66;
  --planet-mid: #285c55;
  --planet-dark: #101b2a;
  --planet-light: #b8f59c;
  --planet-accent-surface: #75f4df;
  --planet-line: rgba(74, 255, 138, 0.15);
  --planet-ring: rgba(74, 255, 138, 0.54);
  --planet-ring-shadow: rgba(117, 244, 223, 0.2);
  --planet-glow: rgba(74, 255, 138, 0.28);
}

.type-alien {
  --planet-body: #8d5bd6;
  --planet-mid: #5639a2;
  --planet-dark: #190f32;
  --planet-light: #ff9cc2;
  --planet-accent-surface: #75f4df;
  --planet-line: rgba(255, 126, 168, 0.16);
  --planet-ring: rgba(255, 126, 168, 0.66);
  --planet-ring-shadow: rgba(117, 244, 223, 0.26);
  --planet-glow: rgba(255, 126, 168, 0.42);
}

.type-alien::after {
  opacity: 0.82;
}

.space-ship {
  position: absolute;
  z-index: 3;
  width: 84px;
  height: 118px;
  image-rendering: pixelated;
  transform: translate(-50%, -50%) rotate(50deg);
  transform-origin: center;
  filter: drop-shadow(8px 8px 0 rgba(0, 0, 0, 0.48)) drop-shadow(0 0 12px rgba(255, 105, 74, 0.25));
}

.space-ship::before {
  position: absolute;
  z-index: 1;
  left: 32px;
  top: 0;
  width: 20px;
  height: 34px;
  background: #fff0a3;
  box-shadow: 0 8px #efd07a, 0 18px #b9774d, 0 34px #eef4ef, 0 44px #6e7580;
  clip-path: polygon(50% 0, 100% 22%, 100% 78%, 72% 100%, 28% 100%, 0 78%, 0 22%);
  content: "";
}

.ship-body {
  position: absolute;
  z-index: 2;
  inset: 22px 0 12px;
}

.ship-body::before,
.ship-body::after {
  position: absolute;
  display: block;
  content: "";
}

.ship-body::before {
  z-index: 2;
  left: 25px;
  top: 10px;
  width: 34px;
  height: 76px;
  background: #b5322f;
  box-shadow: inset -8px 0 #7d1d22, inset 8px 0 #cf5b3f, 0 8px 0 #8f2024, 0 72px 0 #3f3037;
  clip-path: polygon(50% 0, 74% 12%, 100% 56%, 84% 100%, 16% 100%, 0 56%, 26% 12%);
}

.ship-body::after {
  z-index: 3;
  left: 31px;
  top: 31px;
  width: 22px;
  height: 42px;
  background: #c74a36;
  box-shadow: 0 -18px 0 #77171b, 0 -10px 0 #e6c850, 0 48px 0 #312c34;
}

.ship-body span {
  position: absolute;
  display: block;
  image-rendering: pixelated;
}

.ship-body span:nth-child(1) {
  z-index: 4;
  left: 34px;
  top: 42px;
  width: 16px;
  height: 25px;
  background: #51c8c8;
  box-shadow: inset 0 0 0 4px #2a8f95, inset 6px 0 0 #93f0ee;
}

.ship-body span:nth-child(2) {
  z-index: 1;
  left: 4px;
  top: 51px;
  width: 34px;
  height: 42px;
  background: #aa2d2d;
  box-shadow: inset 10px 0 #ded05a, 0 26px 0 #68181e;
  clip-path: polygon(100% 0, 100% 100%, 0 88%, 14% 62%, 54% 22%);
}

.ship-body span:nth-child(3) {
  z-index: 1;
  right: 4px;
  top: 51px;
  width: 34px;
  height: 42px;
  background: #c74a36;
  box-shadow: inset -10px 0 #ded05a, 0 26px 0 #68181e;
  clip-path: polygon(0 0, 46% 22%, 86% 62%, 100% 88%, 0 100%);
}

.engine-flame {
  position: absolute;
  z-index: 4;
  left: 33px;
  top: 100px;
  width: 18px;
  height: 18px;
  background: #fff1a4;
  box-shadow: 0 -8px #d9dcda, 0 -16px #707680, -6px 10px #ffb65c, 6px 10px #ff704a, 0 18px #ffdf76;
  animation: flameBlink 0.28s steps(2) infinite;
}

.ship-eta {
  position: absolute;
  z-index: 5;
  min-width: 94px;
  border: 2px solid #1a2030;
  padding: 6px 7px;
  background: rgba(13, 21, 37, 0.92);
  color: #ffd966;
  font-size: 8px;
  line-height: 1;
  transform: translate(36px, 26px);
  white-space: nowrap;
}

.result-burst {
  position: absolute;
  z-index: 7;
  left: 50%;
  top: 50%;
  display: grid;
  gap: 8px;
  width: min(440px, 100% - 48px);
  border: 2px solid #ffd966;
  padding: 16px;
  background: rgba(13, 21, 37, 0.96);
  text-align: center;
  transform: translate(-50%, -50%);
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.62);
}

.result-burst strong {
  color: #ffd966;
  font-size: 14px;
}

.result-burst span,
.result-burst small {
  color: #dff6ff;
  font-size: 10px;
  line-height: 1.45;
}

.result-burst.is-failed {
  border-color: #ff6b6b;
}

.result-burst.is-failed strong {
  color: #ff6b6b;
}

.artifact-view {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
  padding: 18px;
}

.artifact-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 14px;
}

.artifact-heading {
  min-width: 0;
}

.artifact-heading h2 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 17px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artifact-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(86px, 1fr));
  gap: 8px;
  flex: 0 0 auto;
  min-width: min(390px, 100%);
}

.artifact-summary div {
  display: grid;
  gap: 7px;
  border: 2px solid #263149;
  padding: 10px;
  background: rgba(8, 15, 29, 0.9);
  text-align: right;
}

.artifact-summary span {
  color: #7ecfff;
  font-size: 8px;
  line-height: 1;
}

.artifact-summary strong {
  overflow: hidden;
  color: #ffd966;
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artifact-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(190px, 0.72fr) minmax(280px, 1fr) minmax(220px, 0.72fr);
  gap: 14px;
  min-height: 0;
}

.artifact-collection,
.artifact-detail,
.artifact-log {
  min-width: 0;
  min-height: 0;
}

.artifact-collection {
  display: grid;
  gap: 12px;
  align-content: start;
  overflow: auto;
  padding-right: 2px;
}

.artifact-tile {
  position: relative;
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  grid-template-rows: auto auto auto;
  gap: 6px 12px;
  align-items: center;
  min-width: 0;
  min-height: 118px;
  border: 2px solid #263149;
  padding: 12px;
  background: rgba(8, 15, 29, 0.94);
  color: #dff6ff;
  text-align: left;
  cursor: pointer;
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.45);
}

.artifact-tile.is-active {
  border-color: #7ecfff;
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.45), inset 0 0 0 2px rgba(126, 207, 255, 0.18);
}

.artifact-tile.is-undiscovered {
  border-color: #20283b;
  background: rgba(8, 15, 29, 0.62);
}

.artifact-icon {
  position: relative;
  grid-row: 1/4;
  display: block;
  width: 52px;
  height: 52px;
  image-rendering: pixelated;
}

.artifact-icon i {
  position: absolute;
  display: block;
  width: 14px;
  height: 14px;
  background: #75f4df;
}

.artifact-icon i:nth-child(1) {
  left: 19px;
  top: 4px;
}

.artifact-icon i:nth-child(2) {
  left: 8px;
  top: 22px;
}

.artifact-icon i:nth-child(3) {
  right: 8px;
  top: 22px;
}

.artifact-icon--ruins i {
  background: #ffca7a;
  box-shadow: 0 14px #ffca7a;
}

.artifact-icon--box i {
  background: #ff7ea8;
  box-shadow: 14px 0 #ff7ea8;
}

.artifact-icon--crystal i {
  background: #75f4df;
  transform: rotate(45deg);
}

.artifact-tile.is-undiscovered .artifact-icon,
.artifact-icon.is-muted {
  filter: grayscale(1);
  opacity: 0.3;
}

.artifact-state {
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  border: 2px solid #263149;
  padding: 5px 7px;
  color: #ffd966;
  font-size: 8px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artifact-tile.is-undiscovered .artifact-state {
  color: #596b84;
}

.artifact-tile strong,
.artifact-tile small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.artifact-tile strong {
  color: #f3fbff;
  font-size: 11px;
  line-height: 1.45;
  white-space: normal;
}

.artifact-tile small {
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.45;
  white-space: nowrap;
}

.artifact-detail,
.artifact-log {
  border: 2px solid #1a2030;
  background: rgba(8, 15, 29, 0.9);
}

.artifact-detail {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 14px;
  padding: 16px;
  overflow: hidden;
}

.detail-visual {
  display: grid;
  place-items: center;
  min-height: 132px;
  border: 2px solid #263149;
  background: linear-gradient(rgba(126, 207, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(126, 207, 255, 0.04) 1px, transparent 1px), rgba(5, 8, 20, 0.84);
  background-size: 18px 18px;
}

.artifact-icon-large {
  grid-row: auto;
  width: 52px;
  height: 52px;
  transform: scale(1.5);
  transform-origin: center;
}

.detail-copy {
  min-width: 0;
}

.detail-copy h3 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 15px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-copy p {
  margin-top: 8px;
  color: #8fa5bd;
  font-size: 9px;
  line-height: 1.75;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-content: start;
  overflow: auto;
}

.detail-grid div {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 1px solid #263149;
  padding: 9px;
  background: rgba(5, 8, 20, 0.8);
}

.detail-grid span,
.detail-grid strong {
  overflow: hidden;
  font-size: 8px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-grid span {
  color: #8fa5bd;
}

.detail-grid strong {
  color: #f3fbff;
}

.artifact-log {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  overflow: hidden;
}

.artifact-log-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 10px;
}

.artifact-log-heading strong {
  color: #ffd966;
  font-size: 13px;
  line-height: 1;
}

.artifact-log-list {
  display: grid;
  gap: 8px;
  align-content: start;
  overflow: auto;
}

.artifact-log-row {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
  border: 1px solid #263149;
  padding: 8px;
  background: rgba(5, 8, 20, 0.8);
}

.artifact-dot {
  width: 10px;
  height: 10px;
  background: #75f4df;
  box-shadow: 0 8px #75f4df, 8px 0 #75f4df;
}

.artifact-dot.artifact-icon--ruins {
  background: #ffca7a;
  box-shadow: 0 8px #ffca7a, 8px 0 #ffca7a;
}

.artifact-dot.artifact-icon--box {
  background: #ff7ea8;
  box-shadow: 0 8px #ff7ea8, 8px 0 #ff7ea8;
}

.artifact-log-row div,
.artifact-log-row strong,
.artifact-log-row small,
.artifact-log-row em {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artifact-log-row div {
  display: grid;
  gap: 4px;
}

.artifact-log-row strong {
  color: #f3fbff;
  font-size: 9px;
  line-height: 1.35;
}

.artifact-log-row small,
.artifact-log-row em {
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.35;
}

.artifact-log-row em {
  font-style: normal;
}

.artifact-empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  min-height: 180px;
  color: #8fa5bd;
  text-align: center;
}

.artifact-empty strong {
  color: #596b84;
  font-size: 12px;
}

.artifact-empty span {
  font-size: 9px;
}

.research-view {
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
  overflow: hidden;
  padding: 18px;
}

.research-view::before {
  position: absolute;
  inset: 0;
  content: "";
  background-image: linear-gradient(rgba(126, 207, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(126, 207, 255, 0.035) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}

.research-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 14px;
}

.research-heading {
  min-width: 0;
}

.research-heading h2 {
  color: #f3fbff;
  font-size: 17px;
  line-height: 1.35;
}

.research-heading p {
  margin-top: 8px;
  color: #8fa5bd;
  font-size: 9px;
  line-height: 1.7;
}

.research-header-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(94px, auto));
  gap: 8px;
  flex: 0 0 auto;
  min-width: 0;
}

.research-data-chip,
.research-stat-chip {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 2px solid #263149;
  padding: 10px 12px;
  background: rgba(8, 15, 29, 0.88);
  text-align: right;
}

.research-data-chip {
  min-width: 128px;
  border: 2px solid #ffd966;
  background: rgba(255, 217, 102, 0.08);
}

.research-data-chip span,
.research-stat-chip span {
  color: #ffd966;
  font-size: 8px;
}

.research-stat-chip span {
  color: #7ecfff;
}

.research-data-chip strong,
.research-stat-chip strong {
  color: #f3fbff;
  font-size: 15px;
  line-height: 1;
}

.research-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 216px;
  gap: 18px;
  min-height: 0;
}

.research-map {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  border: 1px solid rgba(126, 207, 255, 0.16);
  padding: 14px 14px 18px;
  background: linear-gradient(rgba(126, 207, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(126, 207, 255, 0.045) 1px, transparent 1px), rgba(5, 8, 20, 0.58);
  background-size: 32px 32px;
  scrollbar-color: #263149 #050814;
}

.research-layer {
  position: relative;
  z-index: 2;
}

.research-root-layer {
  display: flex;
  justify-content: center;
}

.research-root-layer .research-node {
  width: min(360px, 100%);
}

.research-branch-layer {
  display: grid;
  grid-template-columns: repeat(3, minmax(240px, 1fr));
  gap: 16px;
  align-items: start;
  min-width: 780px;
  margin-top: 28px;
}

.research-trunk {
  position: relative;
  z-index: 1;
  height: 34px;
}

.research-trunk::before,
.research-trunk::after,
.leaf-connector::before {
  position: absolute;
  content: "";
  background: repeating-linear-gradient(90deg, #7ecfff 0 8px, transparent 8px 15px);
  opacity: 0.72;
}

.research-trunk::before {
  left: 50%;
  top: 0;
  width: 2px;
  height: 34px;
  background: repeating-linear-gradient(180deg, #7ecfff 0 8px, transparent 8px 15px);
  transform: translateX(-50%);
}

.research-trunk::after {
  left: 17%;
  right: 17%;
  bottom: 0;
  height: 2px;
}

.research-column {
  position: relative;
  display: grid;
  gap: 12px;
  min-width: 0;
  border: 1px solid rgba(126, 207, 255, 0.14);
  padding: 12px;
  background: rgba(8, 15, 29, 0.72);
  box-shadow: inset 0 0 0 1px rgba(126, 207, 255, 0.04);
}

.research-column::before {
  position: absolute;
  left: 50%;
  top: -34px;
  width: 2px;
  height: 34px;
  content: "";
  background: repeating-linear-gradient(180deg, #7ecfff 0 8px, transparent 8px 15px);
  opacity: 0.72;
  transform: translateX(-50%);
}

.research-branch-card {
  position: sticky;
  top: 0;
  z-index: 4;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px 10px;
  align-items: center;
  min-width: 0;
  border: 2px solid #263149;
  padding: 9px 10px;
  background: rgba(5, 8, 20, 0.96);
  box-shadow: 0 8px 0 rgba(5, 8, 20, 0.72);
}

.research-branch-card span,
.research-branch-card strong,
.research-branch-card small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.research-branch-card span {
  color: #7ecfff;
  font-size: 8px;
}

.research-branch-card strong {
  color: #f3fbff;
  font-size: 10px;
  line-height: 1;
}

.research-branch-card small {
  grid-column: 1/-1;
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.4;
}

.research-branch-card.tone-resource {
  border-color: rgba(74, 255, 138, 0.7);
}

.research-branch-card.tone-resource span {
  color: #4aff8a;
}

.research-branch-card.tone-speed {
  border-color: rgba(126, 207, 255, 0.82);
}

.research-branch-card.tone-speed span {
  color: #7ecfff;
}

.research-branch-card.tone-discovery {
  border-color: rgba(255, 202, 122, 0.82);
}

.research-branch-card.tone-discovery span {
  color: #ffca7a;
}

.research-node {
  position: relative;
  display: grid;
  gap: 7px;
  min-width: 0;
  min-height: 112px;
  overflow: hidden;
  border: 2px solid #263149;
  padding: 10px 10px 10px 14px;
  background: rgba(8, 15, 29, 0.94);
  color: #dff6ff;
  text-align: left;
  cursor: pointer;
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.45);
}

.research-node::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  content: "";
  background: #263149;
}

.research-node:hover:not(:disabled) {
  border-color: #7ecfff;
  transform: translate(-1px, -1px);
}

.research-node:disabled {
  cursor: default;
}

.research-node strong,
.research-node small,
.research-node em,
.node-status {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.research-node strong {
  color: #f3fbff;
  font-size: 11px;
  line-height: 1.45;
}

.research-node small {
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.65;
  white-space: normal;
}

.research-node em {
  align-self: end;
  color: #ffd966;
  font-size: 8px;
  font-style: normal;
  line-height: 1.3;
  white-space: nowrap;
}

.node-status {
  width: fit-content;
  border: 2px solid #263149;
  padding: 5px 7px;
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1;
  white-space: nowrap;
}

.research-node.is-completed {
  border-color: #4aff8a;
  background: rgba(74, 255, 138, 0.1);
}

.research-node.is-completed::before {
  background: #4aff8a;
}

.research-node.is-completed .node-status {
  border-color: #4aff8a;
  color: #4aff8a;
}

.research-node.is-available {
  border-color: #ffd966;
}

.research-node.is-available::before {
  background: #ffd966;
}

.research-node.is-ready {
  box-shadow: 5px 5px 0 rgba(0, 0, 0, 0.45), 0 0 18px rgba(255, 217, 102, 0.2);
}

.research-node.is-available .node-status {
  border-color: #ffd966;
  color: #ffd966;
}

.research-node.is-locked {
  border-color: #20283b;
  background: rgba(8, 15, 29, 0.62);
}

.research-node.is-locked::before {
  background: #20283b;
}

.research-node.is-locked strong,
.research-node.is-locked small,
.research-node.is-locked em {
  color: #596b84;
}

.research-leaves {
  position: relative;
  display: grid;
  gap: 8px;
  padding-top: 6px;
}

.research-leaves::before {
  position: absolute;
  top: -6px;
  bottom: 0;
  left: 10px;
  width: 1px;
  content: "";
  background: linear-gradient(180deg, rgba(126, 207, 255, 0.7), rgba(126, 207, 255, 0.1));
}

.leaf-connector {
  position: absolute;
  left: 50%;
  top: -22px;
  width: 2px;
  height: 22px;
  background: repeating-linear-gradient(180deg, #7ecfff 0 8px, transparent 8px 15px);
  opacity: 0.72;
  transform: translateX(-50%);
}

.leaf-connector::before {
  left: -48px;
  right: -48px;
  bottom: 0;
  height: 2px;
}

.research-leaf {
  z-index: 1;
  min-height: 88px;
}

.research-summary {
  display: grid;
  align-content: start;
  gap: 12px;
  min-width: 0;
  max-height: 100%;
  overflow: auto;
  scrollbar-color: #263149 #050814;
}

.summary-block {
  display: grid;
  gap: 10px;
  min-width: 0;
  border: 2px solid #1a2030;
  padding: 12px;
  background: rgba(8, 15, 29, 0.92);
  box-shadow: inset 0 0 0 1px rgba(126, 207, 255, 0.05);
}

.summary-block > strong {
  color: #f3fbff;
  font-size: 18px;
  line-height: 1;
}

.summary-block > small {
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.4;
}

.research-progress {
  height: 11px;
  overflow: hidden;
  border: 1px solid #263149;
  background: #050814;
}

.research-progress span {
  display: block;
  height: 100%;
  min-width: 3px;
  background: repeating-linear-gradient(90deg, #4aff8a 0 7px, #2abf69 7px 14px);
}

.effect-list {
  display: grid;
  gap: 8px;
}

.effect-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
  border: 1px solid #263149;
  padding: 8px;
  background: rgba(5, 8, 20, 0.8);
}

.effect-row span,
.effect-row strong {
  overflow: hidden;
  font-size: 8px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.effect-row span {
  color: #8fa5bd;
}

.effect-row strong {
  color: #596b84;
}

.effect-row.is-active {
  border-color: rgba(74, 255, 138, 0.72);
}

.effect-row.is-active strong {
  color: #4aff8a;
}

.record-view {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
  padding: 18px;
}

.record-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 14px;
}

.record-heading {
  min-width: 0;
}

.record-heading h2 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 17px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(72px, 1fr));
  gap: 8px;
  flex: 0 0 auto;
  min-width: min(430px, 100%);
}

.record-summary-cell {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 2px solid #263149;
  padding: 10px;
  background: rgba(8, 15, 29, 0.9);
  text-align: right;
}

.record-summary-cell span {
  color: #7ecfff;
  font-size: 8px;
  line-height: 1;
}

.record-summary-cell strong {
  overflow: hidden;
  color: #ffd966;
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-summary-cell.tone-success strong {
  color: #4aff8a;
}

.record-summary-cell.tone-danger strong {
  color: #ff6b6b;
}

.record-summary-cell.tone-rare strong {
  color: #ffca7a;
}

.record-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(320px, 1.12fr) minmax(300px, 0.9fr) minmax(220px, 0.62fr);
  gap: 14px;
  min-height: 0;
}

.record-list-panel,
.record-detail-panel,
.record-timeline-panel {
  min-width: 0;
  min-height: 0;
  border: 2px solid #1a2030;
  background: rgba(8, 15, 29, 0.9);
}

.record-list-panel,
.record-timeline-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  overflow: hidden;
}

.record-panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 10px;
}

.record-panel-heading strong {
  color: #ffd966;
  font-size: 13px;
  line-height: 1;
}

.record-list {
  display: grid;
  gap: 8px;
  align-content: start;
  overflow: auto;
  padding-right: 2px;
}

.record-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(122px, auto) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  border: 2px solid #263149;
  padding: 10px;
  background: rgba(5, 8, 20, 0.82);
  color: #dff6ff;
  text-align: left;
  cursor: pointer;
}

.record-row.is-active {
  border-color: #7ecfff;
  box-shadow: inset 0 0 0 2px rgba(126, 207, 255, 0.14);
}

.record-row.is-failed {
  border-color: rgba(255, 107, 107, 0.64);
}

.record-row-main {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.record-status {
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  border: 2px solid #263149;
  padding: 5px 7px;
  color: #4aff8a;
  font-size: 8px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-row.is-failed .record-status {
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.record-row strong,
.record-row small,
.record-row em,
.record-row-rewards span {
  min-width: 0;
  overflow: hidden;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-row strong {
  color: #f3fbff;
  font-size: 10px;
}

.record-row small,
.record-row em {
  color: #8fa5bd;
  font-size: 8px;
}

.record-row em {
  font-style: normal;
}

.record-row-rewards {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.record-row-rewards span {
  color: #ffd966;
  font-size: 8px;
}

.record-detail-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 14px;
  padding: 16px;
  overflow: hidden;
}

.record-detail-empty {
  align-content: center;
}

.record-detail-empty .record-empty {
  min-height: 0;
}

.record-detail-head {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  min-width: 0;
}

.record-planet-sigil {
  display: grid;
  place-items: center;
  width: 70px;
  height: 70px;
  border: 4px solid #dff6ff;
  background: #287fd6;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.5), inset -12px -12px 0 rgba(0, 0, 0, 0.25), inset 10px 10px 0 rgba(255, 255, 255, 0.18);
}

.record-planet-sigil span {
  color: #050814;
  font-size: 16px;
  line-height: 1;
}

.record-planet-sigil.grade-F {
  background: #a6efff;
}

.record-planet-sigil.grade-C {
  background: #77dfff;
}

.record-planet-sigil.grade-B {
  background: #a889ff;
}

.record-planet-sigil.grade-A {
  background: #d1a765;
}

.record-planet-sigil.grade-S {
  background: #ffd966;
}

.record-planet-sigil.grade-X {
  background: #ff6b6b;
}

.record-detail-head h3,
.record-detail-head p {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.record-detail-head h3 {
  color: #f3fbff;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}

.record-detail-head p {
  margin-top: 8px;
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.6;
  white-space: normal;
}

.record-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-content: start;
  overflow: auto;
}

.record-detail-grid div,
.record-reward-strip div {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 1px solid #263149;
  padding: 9px;
  background: rgba(5, 8, 20, 0.8);
}

.record-detail-grid span,
.record-detail-grid strong,
.record-reward-strip span,
.record-reward-strip strong {
  min-width: 0;
  overflow: hidden;
  font-size: 8px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-detail-grid span,
.record-reward-strip span {
  color: #8fa5bd;
}

.record-detail-grid strong {
  color: #f3fbff;
}

.record-reward-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.record-reward-strip strong {
  color: #ffd966;
}

.prestige-log-list {
  display: grid;
  gap: 8px;
  align-content: start;
  overflow: auto;
}

.prestige-log-row {
  display: grid;
  gap: 6px;
  min-width: 0;
  border: 1px solid #263149;
  padding: 9px;
  background: rgba(5, 8, 20, 0.8);
}

.prestige-log-row span,
.prestige-log-row strong,
.prestige-log-row small,
.prestige-log-row em {
  min-width: 0;
  overflow: hidden;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prestige-log-row span {
  color: #7ecfff;
  font-size: 8px;
}

.prestige-log-row strong {
  color: #f3fbff;
  font-size: 10px;
}

.prestige-log-row small,
.prestige-log-row em {
  color: #8fa5bd;
  font-size: 8px;
}

.prestige-log-row em {
  font-style: normal;
}

.record-empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  min-height: 180px;
  color: #8fa5bd;
  text-align: center;
}

.record-empty strong {
  color: #596b84;
  font-size: 12px;
}

.record-empty span {
  font-size: 9px;
}

.prestige-view {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
  padding: 18px;
}

.prestige-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 14px;
}

.prestige-heading {
  min-width: 0;
}

.prestige-heading h2 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 17px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prestige-heading p {
  margin-top: 8px;
  color: #8fa5bd;
  font-size: 9px;
  line-height: 1.7;
}

.prestige-state-chip {
  display: grid;
  flex: 0 0 auto;
  min-width: 132px;
  gap: 7px;
  border: 2px solid #ff6b6b;
  padding: 10px 12px;
  background: rgba(255, 107, 107, 0.08);
  text-align: right;
}

.prestige-state-chip.is-ready {
  border-color: #4aff8a;
  background: rgba(74, 255, 138, 0.08);
}

.prestige-state-chip span {
  color: #ff6b6b;
  font-size: 8px;
  line-height: 1;
}

.prestige-state-chip.is-ready span {
  color: #4aff8a;
}

.prestige-state-chip strong {
  color: #f3fbff;
  font-size: 15px;
  line-height: 1;
}

.prestige-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(260px, 0.86fr) minmax(320px, 1.1fr) minmax(220px, 0.68fr);
  gap: 14px;
  min-height: 0;
}

.prestige-core-panel,
.prestige-matrix,
.prestige-history-panel {
  min-width: 0;
  min-height: 0;
}

.prestige-core-panel {
  display: grid;
  grid-template-rows: minmax(220px, 1fr) auto;
  gap: 14px;
  overflow: hidden;
}

.prestige-orbit {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 0;
  border: 2px solid #1a2030;
  background: radial-gradient(circle at 50% 50%, rgba(255, 217, 102, 0.1), transparent 36%), rgba(8, 15, 29, 0.86);
}

.prestige-core {
  position: relative;
  z-index: 2;
  width: 78px;
  height: 78px;
  border: 4px solid #dff6ff;
  background: #ffd966;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.5), inset -14px -14px 0 rgba(0, 0, 0, 0.22), 0 0 24px rgba(255, 217, 102, 0.32);
}

.prestige-orbit.is-ready .prestige-core {
  background: #4aff8a;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.5), inset -14px -14px 0 rgba(0, 0, 0, 0.22), 0 0 30px rgba(74, 255, 138, 0.44);
}

.prestige-core span {
  position: absolute;
  display: block;
  background: rgba(5, 8, 20, 0.52);
}

.prestige-core span:nth-child(1) {
  left: 16px;
  top: 16px;
  width: 14px;
  height: 14px;
}

.prestige-core span:nth-child(2) {
  right: 14px;
  top: 32px;
  width: 22px;
  height: 12px;
}

.prestige-core span:nth-child(3) {
  left: 34px;
  bottom: 14px;
  width: 12px;
  height: 18px;
}

.prestige-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  border: 2px dashed rgba(126, 207, 255, 0.52);
  transform: translate(-50%, -50%) rotate(-18deg);
}

.prestige-ring-a {
  width: 184px;
  height: 78px;
}

.prestige-ring-b {
  width: 242px;
  height: 112px;
  border-color: rgba(255, 217, 102, 0.42);
  transform: translate(-50%, -50%) rotate(24deg);
}

.prestige-progress-panel,
.prestige-section,
.prestige-history-panel {
  border: 2px solid #1a2030;
  background: rgba(8, 15, 29, 0.9);
}

.prestige-progress-panel {
  display: grid;
  gap: 12px;
  padding: 14px;
}

.prestige-progress-head,
.prestige-destination,
.prestige-section-head,
.bonus-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.prestige-progress-head span,
.prestige-destination span,
.prestige-section-head .eyebrow,
.bonus-row span {
  color: #8fa5bd;
  font-size: 8px;
  line-height: 1.35;
}

.prestige-progress-head strong,
.prestige-destination strong,
.prestige-section-head strong,
.bonus-row strong {
  overflow: hidden;
  color: #ffd966;
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prestige-progress {
  height: 12px;
  overflow: hidden;
  border: 1px solid #263149;
  background: #050814;
}

.prestige-progress span {
  display: block;
  height: 100%;
  min-width: 3px;
  background: repeating-linear-gradient(90deg, #ffd966 0 7px, #ffb45f 7px 14px);
}

.prestige-action {
  min-height: 44px;
  border: 2px solid #5b4631;
  background: #ffd966;
  color: #141008;
  font-size: 10px;
  line-height: 1.3;
  cursor: pointer;
}

.prestige-action.is-ready {
  border-color: #1e713e;
  background: #4aff8a;
  color: #06120b;
}

.prestige-matrix {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 14px;
  overflow: hidden;
}

.prestige-section {
  display: grid;
  gap: 12px;
  min-width: 0;
  padding: 14px;
  overflow: hidden;
}

.prestige-section-head {
  border-bottom: 2px solid #1a2030;
  padding-bottom: 10px;
}

.requirement-grid,
.bonus-grid,
.bonus-list,
.prestige-stat-grid {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.requirement-grid,
.bonus-grid,
.prestige-stat-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.requirement-card,
.bonus-card,
.prestige-stat-grid div {
  display: grid;
  gap: 7px;
  min-width: 0;
  border: 1px solid #263149;
  padding: 10px;
  background: rgba(5, 8, 20, 0.8);
}

.requirement-card.is-complete,
.bonus-card.is-active,
.bonus-row.is-active {
  border-color: rgba(74, 255, 138, 0.7);
}

.requirement-card span,
.requirement-card strong,
.requirement-card small,
.bonus-card span,
.bonus-card strong,
.prestige-stat-grid span,
.prestige-stat-grid strong {
  min-width: 0;
  overflow: hidden;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.requirement-card span,
.requirement-card small,
.bonus-card span,
.prestige-stat-grid span {
  color: #8fa5bd;
  font-size: 8px;
}

.requirement-card strong,
.bonus-card strong,
.prestige-stat-grid strong {
  color: #f3fbff;
  font-size: 11px;
}

.requirement-card.is-complete strong,
.bonus-card.is-active strong,
.bonus-row.is-active strong {
  color: #4aff8a;
}

.bonus-list {
  align-content: start;
  overflow: auto;
}

.bonus-row {
  border: 1px solid #263149;
  padding: 9px;
  background: rgba(5, 8, 20, 0.8);
}

.prestige-history-panel {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  overflow: hidden;
}

.prestige-empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  min-height: 180px;
  color: #8fa5bd;
  text-align: center;
}

.prestige-empty strong {
  color: #596b84;
  font-size: 12px;
}

.prestige-empty span {
  font-size: 9px;
}

.upgrade-action {
  width: 100%;
  min-height: 32px;
  border: 2px solid #1e713e;
  background: #4aff8a;
  color: #06120b;
  font-size: 11px;
  cursor: pointer;
}

.upgrade-action.is-shield {
  border-color: #ff6b6b;
  background: #ff6b6b;
  color: #210707;
}

button:disabled {
  cursor: not-allowed;
  filter: grayscale(0.55);
  opacity: 0.5;
}

.quest-dock {
  position: fixed;
  z-index: 34;
  right: 22px;
  bottom: 104px;
  display: grid;
  gap: 10px;
  width: min(330px, 100vw - 32px);
  border: 2px solid #263149;
  padding: 10px;
  background: rgba(8, 15, 29, 0.96);
  image-rendering: pixelated;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.58), inset 0 0 0 2px rgba(126, 207, 255, 0.07);
}

.quest-dock.is-completing {
  border-color: #4aff8a;
  animation: questCompletePulse 0.55s steps(2) 4;
}

.quest-dock-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px;
  gap: 8px;
  align-items: stretch;
}

.quest-dock-heading,
.quest-fold,
.quest-current-card {
  border: 2px solid #1a2030;
  background: #0d1525;
  color: #dff6ff;
  cursor: pointer;
}

.quest-dock-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  min-height: 34px;
  padding: 7px 9px;
  text-align: left;
}

.quest-kicker,
.quest-dock-heading strong,
.quest-fold,
.quest-current-meta span,
.quest-current-meta strong,
.quest-progress-row span,
.quest-progress-row strong,
.quest-reward-line span,
.quest-reward-line strong {
  overflow: hidden;
  font-size: 8px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-kicker {
  color: #7ecfff;
}

.quest-dock-heading strong,
.quest-current-meta strong,
.quest-progress-row strong {
  color: #ffd966;
}

.quest-fold {
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 34px;
  color: #7ecfff;
  font-size: 14px;
  line-height: 1;
}

.quest-current-card {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 10px;
  text-align: left;
}

.quest-dock.is-collapsed .quest-current-card {
  padding: 8px 10px;
}

.quest-dock.is-collapsed .quest-progress-row,
.quest-dock.is-collapsed .quest-progress-bar,
.quest-dock.is-collapsed .quest-reward-line {
  display: none;
}

.quest-current-meta,
.quest-progress-row,
.quest-reward-line,
.quest-modal-head,
.quest-category-head,
.quest-list-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.quest-current-meta span,
.quest-progress-row span,
.quest-reward-line span {
  color: #8fa5bd;
}

.quest-current-card h3 {
  min-width: 0;
  overflow: hidden;
  color: #f3fbff;
  font-size: 11px;
  line-height: 1.55;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-dock.is-collapsed .quest-current-card h3 {
  font-size: 9px;
}

.quest-progress-bar,
.quest-modal-progress,
.quest-list-progress {
  height: 10px;
  overflow: hidden;
  border: 1px solid #263149;
  background: #050814;
}

.quest-progress-bar span,
.quest-modal-progress span,
.quest-list-progress span {
  display: block;
  height: 100%;
  min-width: 3px;
  background: repeating-linear-gradient(90deg, #4aff8a 0 7px, #2abf69 7px 14px);
}

.quest-reward-line {
  border-top: 1px solid #1a2030;
  padding-top: 8px;
}

.quest-reward-line strong {
  color: #4aff8a;
}

.quest-complete-toast {
  position: fixed;
  z-index: 58;
  right: 22px;
  bottom: 300px;
  display: grid;
  gap: 8px;
  width: min(360px, 100vw - 32px);
  border: 2px solid #4aff8a;
  padding: 14px;
  background: #0d1525;
  image-rendering: pixelated;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.6), inset 0 0 0 2px rgba(74, 255, 138, 0.08);
  animation: questToastIn 0.28s steps(4);
}

.quest-complete-toast span {
  color: #4aff8a;
  font-size: 8px;
}

.quest-complete-toast h3 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 12px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-complete-toast p,
.quest-complete-toast small {
  overflow: hidden;
  color: #ffd966;
  font-size: 8px;
  line-height: 1.55;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-complete-toast small {
  color: #8fa5bd;
}

.quest-list-backdrop {
  z-index: 56;
}

.quest-modal {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 14px;
  width: min(920px, 100%);
  max-height: min(760px, 100vh - 40px);
  border: 2px solid #7ecfff;
  padding: 18px;
  background: #0d1525;
  box-shadow: 8px 8px 0 rgba(0, 0, 0, 0.58), inset 0 0 0 2px rgba(126, 207, 255, 0.08);
}

.quest-modal-head {
  grid-template-columns: minmax(0, 1fr) auto 34px;
  border-bottom: 2px solid #1a2030;
  padding-bottom: 12px;
}

.quest-modal-head h2 {
  overflow: hidden;
  color: #f3fbff;
  font-size: 16px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-modal-count {
  display: grid;
  gap: 6px;
  min-width: 112px;
  border: 2px solid #263149;
  padding: 8px 10px;
  text-align: right;
}

.quest-modal-count span {
  color: #7ecfff;
  font-size: 8px;
}

.quest-modal-count strong {
  color: #ffd966;
  font-size: 12px;
}

.quest-modal-close {
  display: grid;
  place-items: center;
  min-width: 34px;
  min-height: 34px;
  border: 2px solid #ff6b6b;
  background: #0d1525;
  color: #ff6b6b;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.quest-category-list {
  display: grid;
  gap: 12px;
  min-height: 0;
  overflow: auto;
  padding-right: 2px;
}

.quest-category {
  display: grid;
  gap: 10px;
  min-width: 0;
  border: 2px solid #1a2030;
  padding: 12px;
  background: rgba(5, 8, 20, 0.72);
}

.quest-category-head {
  border-bottom: 2px solid #1a2030;
  padding-bottom: 10px;
}

.quest-category-head div {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.quest-category-icon {
  font-size: 13px;
  line-height: 1;
}

.quest-category-head strong,
.quest-category-head em {
  overflow: hidden;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-category-head strong {
  color: #f3fbff;
  font-size: 11px;
}

.quest-category-head em {
  color: #ffd966;
  font-size: 9px;
  font-style: normal;
}

.quest-list {
  display: grid;
  gap: 7px;
}

.quest-list-row {
  grid-template-columns: 46px minmax(0, 1fr) minmax(82px, auto);
  align-items: start;
  border: 1px solid #263149;
  padding: 9px;
  background: rgba(8, 15, 29, 0.86);
}

.quest-list-row.is-active {
  border-color: #ffd966;
  box-shadow: inset 0 0 0 2px rgba(255, 217, 102, 0.1);
}

.quest-list-row.is-completed {
  border-color: rgba(74, 255, 138, 0.7);
}

.quest-list-row.is-locked {
  opacity: 0.52;
}

.quest-list-number {
  color: #7ecfff;
  font-size: 8px;
  line-height: 1.5;
}

.quest-list-main {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.quest-list-main strong,
.quest-list-main small,
.quest-list-state span {
  overflow: hidden;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quest-list-main strong {
  color: #f3fbff;
  font-size: 9px;
}

.quest-list-main small {
  color: #8fa5bd;
  font-size: 8px;
}

.quest-list-state span {
  display: block;
  color: #ffd966;
  font-size: 8px;
  text-align: right;
}

.quest-list-row.is-completed .quest-list-state span {
  color: #4aff8a;
}

.quest-list-row.is-locked .quest-list-state span {
  color: #596b84;
}

.bottom-tabs {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  min-height: 0;
}

.bottom-tabs button {
  position: relative;
  min-width: 0;
  overflow: hidden;
  padding: 16px 8px 10px;
  color: #8fa5bd;
  font-size: 11px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bottom-tabs button::before {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  content: "";
  background: transparent;
}

.bottom-tabs button.is-active {
  color: #f3fbff;
  border-color: #7ecfff;
}

.bottom-tabs button.is-active::before {
  background: #7ecfff;
  box-shadow: 0 0 14px rgba(126, 207, 255, 0.8);
}

.astrova-shell.is-tutorial-active .is-tutorial-highlight {
  position: relative;
  z-index: 72;
  border-color: #75f4df;
  pointer-events: none;
  box-shadow: 0 0 0 4px rgba(117, 244, 223, 0.82), 0 0 30px rgba(117, 244, 223, 0.42), inset 0 0 0 2px rgba(117, 244, 223, 0.22);
  animation: tutorialPulse 1.05s steps(2) infinite;
}

.astrova-shell.is-tutorial-active .is-tutorial-scope {
  position: relative;
  z-index: auto;
}

.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .planet-title,
.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .orbit-line,
.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .planet-pixel,
.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .space-ship,
.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .ship-eta,
.astrova-shell.is-tutorial-active .space-view:not(.is-tutorial-highlight) .result-burst {
  display: none;
}

.tutorial-overlay {
  position: static;
  pointer-events: none;
}

.tutorial-dim {
  position: fixed;
  z-index: 60;
  inset: 0;
  background: rgba(3, 5, 14, 0.92);
  pointer-events: auto;
}

.tutorial-skip {
  position: fixed;
  z-index: 96;
  top: 22px;
  right: 22px;
  min-height: 38px;
  border: 2px solid #75f4df;
  padding: 0 14px;
  background: #0d1525;
  color: #75f4df;
  font-size: 10px;
  line-height: 1.2;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 4px 4px 0 rgba(0, 0, 0, 0.58);
}

.tutorial-guide {
  position: fixed;
  z-index: 94;
  left: 50%;
  bottom: 28px;
  display: grid;
  grid-template-columns: 96px minmax(0, 620px);
  gap: 18px;
  align-items: end;
  width: min(760px, 100vw - 40px);
  transform: translateX(-50%);
  pointer-events: none;
}

.tutorial-guide.target-specs {
  left: 290px;
  top: 50%;
  bottom: auto;
  width: min(650px, 100vw - 340px);
  transform: translateY(-50%);
}

.tutorial-guide.target-upgrades {
  left: 290px;
  top: 50%;
  bottom: auto;
  width: min(650px, 100vw - 340px);
  transform: translateY(-50%);
}

.tutorial-guide.target-tabs {
  top: 94px;
  bottom: auto;
}

.tutorial-guide.target-research-panel,
.tutorial-guide.target-research-map,
.tutorial-guide.target-artifact-panel,
.tutorial-guide.target-artifact-collection,
.tutorial-guide.target-artifact-detail,
.tutorial-guide.target-record-panel,
.tutorial-guide.target-record-list,
.tutorial-guide.target-record-detail,
.tutorial-guide.target-prestige-panel,
.tutorial-guide.target-prestige-progress,
.tutorial-guide.target-prestige-requirements,
.tutorial-guide.target-prestige-bonuses {
  bottom: 86px;
}

.tutorial-guide.target-research-data,
.tutorial-guide.target-artifact-summary,
.tutorial-guide.target-record-summary,
.tutorial-guide.target-prestige-status {
  top: 174px;
  right: 360px;
  bottom: auto;
  left: auto;
  width: min(620px, 100vw - 440px);
  transform: none;
}

.tutorial-guide.target-research-summary,
.tutorial-guide.target-artifact-log,
.tutorial-guide.target-record-timeline,
.tutorial-guide.target-prestige-history {
  top: 50%;
  bottom: auto;
  left: 180px;
  width: min(650px, 100vw - 470px);
  transform: translateY(-50%);
}

.tutorial-guide.target-all {
  top: 50%;
  bottom: auto;
  transform: translate(-50%, -50%);
}

.tutorial-ai {
  position: relative;
  width: 88px;
  height: 118px;
  image-rendering: pixelated;
  animation: aiHover 1.8s steps(4) infinite;
}

.ai-antenna {
  position: absolute;
  left: 41px;
  top: 0;
  width: 6px;
  height: 18px;
  background: #75f4df;
  box-shadow: -10px -6px 0 #ffd966, 10px -6px 0 #ff6b6b;
}

.ai-head {
  position: absolute;
  left: 14px;
  top: 18px;
  width: 60px;
  height: 46px;
  border: 4px solid #dff6ff;
  background: #7ecfff;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.55), inset -10px -8px 0 rgba(13, 21, 37, 0.24), inset 8px 8px 0 rgba(255, 255, 255, 0.22);
}

.ai-head span {
  position: absolute;
  display: block;
}

.ai-head span:nth-child(1),
.ai-head span:nth-child(2) {
  top: 16px;
  width: 9px;
  height: 9px;
  background: #050814;
  box-shadow: 0 0 0 3px #75f4df;
}

.ai-head span:nth-child(1) {
  left: 12px;
}

.ai-head span:nth-child(2) {
  right: 12px;
}

.ai-head span:nth-child(3) {
  left: 21px;
  bottom: 9px;
  width: 16px;
  height: 4px;
  background: #050814;
}

.ai-body {
  position: absolute;
  left: 22px;
  top: 66px;
  width: 44px;
  height: 38px;
  border: 4px solid #dff6ff;
  background: #253a58;
  box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.55), inset 8px 0 0 rgba(117, 244, 223, 0.24);
}

.ai-body span {
  position: absolute;
  display: block;
}

.ai-body span:nth-child(1) {
  left: 14px;
  top: 9px;
  width: 10px;
  height: 10px;
  background: #4aff8a;
  box-shadow: 0 0 12px rgba(74, 255, 138, 0.72);
}

.ai-body span:nth-child(2),
.ai-body span:nth-child(3) {
  top: 12px;
  width: 12px;
  height: 8px;
  background: #75f4df;
}

.ai-body span:nth-child(2) {
  left: -18px;
}

.ai-body span:nth-child(3) {
  right: -18px;
}

.ai-base {
  position: absolute;
  left: 30px;
  bottom: 0;
  width: 28px;
  height: 10px;
  background: #dff6ff;
  box-shadow: -12px 0 0 #7ecfff, 12px 0 0 #7ecfff;
}

.tutorial-bubble {
  position: relative;
  display: grid;
  gap: 14px;
  min-width: 0;
  border: 3px solid #75f4df;
  padding: 18px;
  background: #0d1525;
  color: #dff6ff;
  pointer-events: auto;
  box-shadow: 7px 7px 0 rgba(0, 0, 0, 0.62), inset 0 0 0 2px rgba(117, 244, 223, 0.08);
}

.tutorial-bubble::before {
  position: absolute;
  left: -15px;
  bottom: 26px;
  width: 18px;
  height: 18px;
  border-left: 3px solid #75f4df;
  border-bottom: 3px solid #75f4df;
  content: "";
  background: #0d1525;
  transform: rotate(45deg);
}

.tutorial-meta,
.tutorial-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.tutorial-meta span {
  overflow: hidden;
  color: #75f4df;
  font-size: 9px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tutorial-meta strong {
  color: #ffd966;
  font-size: 10px;
  line-height: 1;
  white-space: nowrap;
}

.tutorial-bubble p {
  color: #f3fbff;
  font-size: 12px;
  line-height: 1.8;
  white-space: pre-line;
}

.tutorial-next {
  min-width: 108px;
  min-height: 42px;
  margin-left: auto;
  border: 2px solid #1e713e;
  background: #4aff8a;
  color: #06120b;
  font-size: 11px;
  line-height: 1.2;
  cursor: pointer;
}

.modal-backdrop {
  position: fixed;
  z-index: 50;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(5, 8, 20, 0.78);
}

.prestige-dialog {
  display: grid;
  gap: 14px;
  width: min(460px, 100%);
  border: 2px solid #7ecfff;
  padding: 20px;
  background: #0d1525;
  box-shadow: 8px 8px 0 rgba(0, 0, 0, 0.58), inset 0 0 0 2px rgba(126, 207, 255, 0.08);
}

.prestige-dialog h2 {
  color: #f3fbff;
  font-size: 16px;
  line-height: 1.4;
}

.prestige-dialog p {
  color: #8fa5bd;
  font-size: 9px;
  line-height: 1.8;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
}

.dialog-button {
  min-width: 96px;
  min-height: 40px;
  border: 2px solid #263149;
  background: #111d32;
  color: #dff6ff;
  font-size: 10px;
}

.dialog-button.is-danger {
  border-color: #ff6b6b;
  background: #ff6b6b;
  color: #210707;
}

.dialog-button.is-primary {
  border-color: #1e713e;
  background: #4aff8a;
  color: #06120b;
}

@keyframes onlineBlink {
  50% {
    opacity: 0.35;
  }
}
@keyframes twinkleA {
  50% {
    opacity: 0.45;
  }
}
@keyframes twinkleB {
  50% {
    opacity: 0.28;
  }
}
@keyframes pixelDrift {
  100% {
    transform: translate(20px, 14px);
  }
}
@keyframes flameBlink {
  50% {
    height: 28px;
    transform: translateY(4px);
    box-shadow: 0 -8px #d9dcda, 0 -16px #707680, -8px 14px #ff704a, 8px 14px #ffb65c, 0 24px #fff1a4;
  }
}
@keyframes snowFall {
  100% {
    transform: translate(18px, 34px);
  }
}
@keyframes lavaPulse {
  50% {
    opacity: 0.34;
  }
}
@keyframes gasFloat {
  50% {
    transform: translateY(-12px);
  }
}
@keyframes dustDrift {
  100% {
    transform: translateX(44px);
  }
}
@keyframes oceanWave {
  50% {
    transform: translateX(26px);
  }
}
@keyframes bubbleRise {
  100% {
    transform: translateY(-28px);
    opacity: 0.35;
  }
}
@keyframes alienSignal {
  50% {
    opacity: 0.28;
    transform: translateY(-6px);
  }
}
@keyframes warningPulse {
  50% {
    box-shadow: inset 0 0 0 2px rgba(255, 107, 107, 0.45);
  }
}
@keyframes questCompletePulse {
  50% {
    box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.58), 0 0 26px rgba(74, 255, 138, 0.52), inset 0 0 0 2px rgba(74, 255, 138, 0.22);
  }
}
@keyframes questToastIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes tutorialPulse {
  50% {
    box-shadow: 0 0 0 6px rgba(117, 244, 223, 0.55), 0 0 36px rgba(117, 244, 223, 0.58), inset 0 0 0 2px rgba(117, 244, 223, 0.32);
  }
}
@keyframes aiHover {
  50% {
    transform: translateY(-6px);
  }
}
@media (max-width: 980px) {
  .astrova-shell {
    height: auto;
    min-height: 100vh;
    overflow: auto;
    grid-template-rows: auto auto auto auto;
  }
  .main-area {
    grid-template-columns: 1fr;
  }
  .left-panel {
    width: 100%;
  }
  .spec-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .space-view {
    min-height: 520px;
  }
  .artifact-view,
  .research-view,
  .record-view,
  .prestige-view {
    min-height: 640px;
  }
  .artifact-content,
  .research-content,
  .record-content,
  .prestige-content {
    grid-template-columns: 1fr;
  }
  .artifact-collection {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: visible;
  }
  .artifact-log {
    min-height: 260px;
  }
  .research-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .research-header-stats {
    grid-template-columns: repeat(3, minmax(84px, 1fr));
  }
  .record-timeline-panel,
  .prestige-history-panel {
    min-height: 240px;
  }
  .planet-title {
    left: 18px;
    right: 18px;
    max-width: none;
    transform: none;
  }
  .quest-dock,
  .quest-complete-toast {
    right: 16px;
  }
  .quest-complete-toast {
    bottom: 276px;
  }
  .tutorial-guide,
  .tutorial-guide.target-specs,
  .tutorial-guide.target-upgrades,
  .tutorial-guide.target-tabs,
  .tutorial-guide.target-research-panel,
  .tutorial-guide.target-research-data,
  .tutorial-guide.target-research-map,
  .tutorial-guide.target-research-summary,
  .tutorial-guide.target-artifact-panel,
  .tutorial-guide.target-artifact-summary,
  .tutorial-guide.target-artifact-collection,
  .tutorial-guide.target-artifact-detail,
  .tutorial-guide.target-artifact-log,
  .tutorial-guide.target-record-panel,
  .tutorial-guide.target-record-summary,
  .tutorial-guide.target-record-list,
  .tutorial-guide.target-record-detail,
  .tutorial-guide.target-record-timeline,
  .tutorial-guide.target-prestige-panel,
  .tutorial-guide.target-prestige-status,
  .tutorial-guide.target-prestige-progress,
  .tutorial-guide.target-prestige-requirements,
  .tutorial-guide.target-prestige-bonuses,
  .tutorial-guide.target-prestige-history,
  .tutorial-guide.target-all {
    top: auto;
    right: auto;
    bottom: 16px;
    left: 16px;
    width: calc(100vw - 32px);
    transform: none;
  }
  .tutorial-guide.target-tabs {
    top: 88px;
    bottom: auto;
  }
}
@media (max-width: 620px) {
  .top-header {
    padding: 10px 12px;
  }
  .tutorial-replay {
    min-width: 112px;
    padding: 8px 10px;
  }
  .resource-bar,
  .bottom-tabs,
  .spec-list {
    grid-template-columns: 1fr;
  }
  .resource-cell + .resource-cell {
    border-left: 0;
    border-top: 2px solid #1a2030;
  }
  .planet-pixel {
    right: 22px;
    top: 74px;
    width: 116px;
  }
  .space-ship {
    width: 84px;
    height: 118px;
    transform: translate(-50%, -50%) rotate(50deg) scale(0.78);
  }
  .artifact-header,
  .artifact-summary,
  .artifact-collection,
  .detail-grid,
  .research-header,
  .research-summary,
  .research-branch-layer,
  .record-header,
  .record-summary,
  .record-detail-grid,
  .record-reward-strip,
  .prestige-header,
  .requirement-grid,
  .bonus-grid,
  .prestige-stat-grid {
    grid-template-columns: 1fr;
  }
  .artifact-header,
  .research-header,
  .record-header,
  .prestige-header {
    display: grid;
  }
  .artifact-summary,
  .record-summary {
    min-width: 0;
  }
  .artifact-summary div,
  .record-summary-cell {
    text-align: left;
  }
  .artifact-tile {
    grid-template-columns: 52px minmax(0, 1fr);
  }
  .research-header-stats {
    grid-template-columns: 1fr;
  }
  .research-data-chip,
  .research-stat-chip {
    width: 100%;
    text-align: left;
  }
  .research-trunk,
  .research-column::before,
  .leaf-connector {
    display: none;
  }
  .research-branch-layer {
    min-width: 0;
    margin-top: 14px;
  }
  .research-branch-card {
    position: static;
  }
  .record-row {
    grid-template-columns: 1fr;
  }
  .record-detail-head {
    grid-template-columns: 1fr;
  }
  .dialog-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .quest-dock {
    right: 12px;
    bottom: 12px;
  }
  .quest-complete-toast {
    right: 12px;
    bottom: 154px;
  }
  .quest-modal {
    padding: 14px;
  }
  .quest-modal-head,
  .quest-list-row,
  .quest-current-meta,
  .quest-progress-row,
  .quest-reward-line {
    grid-template-columns: 1fr;
  }
  .quest-modal-count,
  .quest-list-state span {
    text-align: left;
  }
  .dialog-button {
    width: 100%;
  }
  .tutorial-skip {
    top: 12px;
    right: 12px;
    min-height: 34px;
    padding: 0 10px;
    font-size: 9px;
  }
  .tutorial-guide,
  .tutorial-guide.target-specs,
  .tutorial-guide.target-upgrades,
  .tutorial-guide.target-tabs,
  .tutorial-guide.target-research-panel,
  .tutorial-guide.target-research-data,
  .tutorial-guide.target-research-map,
  .tutorial-guide.target-research-summary,
  .tutorial-guide.target-artifact-panel,
  .tutorial-guide.target-artifact-summary,
  .tutorial-guide.target-artifact-collection,
  .tutorial-guide.target-artifact-detail,
  .tutorial-guide.target-artifact-log,
  .tutorial-guide.target-record-panel,
  .tutorial-guide.target-record-summary,
  .tutorial-guide.target-record-list,
  .tutorial-guide.target-record-detail,
  .tutorial-guide.target-record-timeline,
  .tutorial-guide.target-prestige-panel,
  .tutorial-guide.target-prestige-status,
  .tutorial-guide.target-prestige-progress,
  .tutorial-guide.target-prestige-requirements,
  .tutorial-guide.target-prestige-bonuses,
  .tutorial-guide.target-prestige-history,
  .tutorial-guide.target-all {
    grid-template-columns: 1fr;
    gap: 8px;
    bottom: 12px;
    left: 12px;
    width: calc(100vw - 24px);
  }
  .tutorial-guide.target-tabs {
    top: 58px;
    bottom: auto;
  }
  .tutorial-ai {
    width: 70px;
    height: 88px;
    transform: scale(0.74);
    transform-origin: left bottom;
  }
  .tutorial-bubble {
    padding: 14px;
  }
  .tutorial-bubble::before {
    display: none;
  }
  .tutorial-meta,
  .tutorial-actions {
    display: grid;
  }
  .tutorial-bubble p {
    font-size: 10px;
    line-height: 1.75;
  }
  .tutorial-next {
    width: 100%;
    margin-left: 0;
  }
}`],
})
export class PageHomeComponent implements OnInit, OnDestroy {
    public readonly tickIntervalMs = TICK_INTERVAL_MS;
    public readonly autoSaveIntervalSeconds = AUTO_SAVE_INTERVAL_MS / 1000;
    public readonly maxOfflineHours = MAX_OFFLINE_SECONDS / 3600;
    public state: GameState = loadGameState();
    public activeTab: ActiveTab = "exploration";
    public offlineReport: OfflineReport | null = null;
    public showOfflineReport = false;
    public offlineReportLines: string[] = [];
    public latestPlanet: DiscoveredPlanetRecord | null = null;
    public resourceViews: ResourceView[] = [];
    public specViews: SpecView[] = [];
    public researchTree: ResearchTreeNode | null = null;
    public researchEffectViews: ResearchEffectView[] = [];
    public completedResearchCount = 0;
    public availableResearchCount = 0;
    public totalResearchCount = Object.keys(RESEARCH_DEFINITIONS).length;
    public artifactViews: ArtifactView[] = [];
    public selectedArtifactKey: ArtifactKey = "ancient_ruins";
    public selectedArtifactView: ArtifactView | null = null;
    public artifactLogEntries: ArtifactLogEntryView[] = [];
    public logEntries: LogEntryView[] = [];
    public recordSummaryViews: RecordSummaryView[] = [];
    public selectedLogId = "";
    public selectedLogEntry: LogEntryView | null = null;
    public prestigeLogEntries: PrestigeLogEntryView[] = [];
    public prestigeRequirementViews: PrestigeRequirementView[] = [];
    public prestigeBonusViews: PrestigeBonusView[] = [];
    public prestigeRewardViews: PrestigeBonusView[] = [];
    public recentResult: ExpeditionResultView | null = null;
    public activeQuestView: QuestView | null = null;
    public questCategoryViews: QuestCategoryView[] = [];
    public completedQuestCount = 0;
    public totalQuestCount = GUIDE_QUEST_DEFINITIONS.length;
    public questPanelCollapsed = false;
    public showQuestList = false;
    public questCompletionPopup: QuestCompletionPopupView | null = null;
    public questPanelPulse = false;
    public lastActionMessage = "AUTO EXPLORATION ONLINE";
    public showPrestigeDialog = false;
    public prestigeDialogMode: PrestigeDialogMode = "confirm";
    public prestigeMonologue = "";
    public shipX = 14;
    public shipY = 78;
    public shipEtaText = "ETA 00:18";
    public showTutorial = false;
    public tutorialStepIndex = 0;
    public tutorialScope: TutorialScope = "main";

    private tickTimer: ReturnType<typeof setInterval> | null = null;
    private saveTimer: ReturnType<typeof setInterval> | null = null;
    private resultTimer: ReturnType<typeof setTimeout> | null = null;
    private questCompletionTimer: ReturnType<typeof setTimeout> | null = null;
    private animationFrameId: number | null = null;
    private shipAnimationStart = 0;
    private readonly shipFlightMs = 18_000;
    private readonly completedTutorialScopes = new Set<TutorialScope>();
    private readonly handleBeforeUnload = (): void => {
        saveGameState(this.state);
    };
    private readonly handleDevelopmentReset = (): GameState => {
        this.state = resetAllGameDataForDevelopment(new Date());
        this.offlineReport = null;
        this.showOfflineReport = false;
        this.offlineReportLines = [];
        this.recentResult = null;
        this.questCompletionPopup = null;
        this.questPanelPulse = false;
        this.lastActionMessage = "DEVELOPMENT RESET COMPLETE";
        this.refreshViewModels();
        return this.state;
    };

    constructor(@Inject( Router) private readonly router: Router) {}

    public ngOnInit(): void {
        if (this.shouldEnterAccessPage()) {
            void this.router.navigateByUrl("/access", { replaceUrl: true });
            return;
        }

        this.state = loadGameState();
        this.offlineReport = applyOfflineProgress(this.state, new Date());
        this.offlineReportLines = this.buildOfflineReportLines(this.offlineReport);
        this.showOfflineReport = this.offlineReport.elapsedSeconds > 0;
        if (this.showOfflineReport) {
            this.lastActionMessage = `OFFLINE REPORT: ${this.offlineReport.planetsDiscovered} PLANETS / +${this.formatCompactValue(this.offlineReport.resourcesGained.explorationData)} DATA`;
        }

        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
        this.startAutoTick();
        this.startShipAnimation();
        this.initializeTutorial();

        if (typeof window !== "undefined") {
            window.addEventListener("beforeunload", this.handleBeforeUnload);
            window.resetAstrovaGameForDevelopment = this.handleDevelopmentReset;
        }
    }

    public ngOnDestroy(): void {
        this.stopAutoTick();
        this.stopShipAnimation();
        if (this.resultTimer) {
            clearTimeout(this.resultTimer);
            this.resultTimer = null;
        }
        if (this.questCompletionTimer) {
            clearTimeout(this.questCompletionTimer);
            this.questCompletionTimer = null;
        }
        saveGameState(this.state);
        if (typeof window !== "undefined") {
            window.removeEventListener("beforeunload", this.handleBeforeUnload);
            if (window.resetAstrovaGameForDevelopment === this.handleDevelopmentReset) {
                delete window.resetAstrovaGameForDevelopment;
            }
        }
    }

    private shouldEnterAccessPage(): boolean {
        if (typeof window === "undefined") {
            return false;
        }

        try {
            const raw = window.localStorage.getItem(ACCESS_PROFILE_STORAGE_KEY);
            if (!raw) return true;

            const profile = JSON.parse(raw) as { nickname?: string; shipId?: string; probeId?: string };
            return !profile.nickname || !profile.shipId || !profile.probeId;
        } catch {
            return true;
        }
    }

    public get currentPlanetName(): string {
        return this.currentPlanet?.name || "UNKNOWN";
    }

    public get currentPlanetGrade(): string {
        return this.currentPlanet?.grade || "-";
    }

    public get currentPlanetTypeLabel(): string {
        const type = this.currentPlanet?.type;
        return type ? PLANET_TYPE_LABELS[type] || type : "-";
    }

    public get currentLifeSignalLabel(): string {
        const signal = this.currentPlanet?.lifeSignal;
        return signal ? LIFE_SIGNAL_LABELS[signal] || signal : "-";
    }

    public get currentHazardLabel(): string {
        const hazards = this.currentPlanet?.hazards || [];
        if (hazards.length === 0) {
            return "안정";
        }

        return hazards.map((hazard) => HAZARD_LABELS[hazard.type] || hazard.type).join(" / ");
    }

    public get currentRiskLabel(): string {
        const requiredShield = this.currentRequiredShieldPower;
        if (requiredShield <= 0) {
            return "LOW";
        }

        const survivalPower = this.state.run.probe.cachedEffectiveStats.survivalPower;
        return survivalPower >= requiredShield ? "CONTROLLED" : "CRITICAL";
    }

    public get currentRequiredShieldPower(): number {
        const planet = this.currentPlanet;
        if (!planet) {
            return 0;
        }

        const hazardRequirement = planet.hazards.reduce((max, hazard) => {
            return Math.max(max, hazard.requiredShieldPower);
        }, 0);
        return Math.max(hazardRequirement, PLANET_GRADE_DEFINITIONS[planet.grade].baseShieldRequirement);
    }

    public get progressPercent(): number {
        const progress = this.state.run.currentExpedition.progress;
        return formatPercent(progress.required > 0 ? progress.current / progress.required : 0);
    }

    public get progressLabel(): string {
        const progress = this.state.run.currentExpedition.progress;
        return `${this.formatCompactValue(progress.current)} / ${this.formatCompactValue(progress.required)}`;
    }

    public get discoveredCount(): number {
        return this.state.archive.discoveredPlanets.order.length;
    }

    public get artifactCount(): number {
        return Object.keys(this.state.archive.artifactCollection.byInstanceId).length;
    }

    public get artifactTypeCount(): number {
        return ARTIFACT_DEFINITIONS.length;
    }

    public get discoveredArtifactTypeCount(): number {
        return this.artifactViews.filter((artifact) => artifact.discovered).length;
    }

    public get artifactScanChanceLabel(): string {
        const chance = this.state.run.probe.cachedEffectiveStats.rareDiscoveryChanceBonus * 100;
        return `${Math.round(chance * 10) / 10}%`;
    }

    public get offlineReportHours(): string {
        if (!this.offlineReport) {
            return "0";
        }

        return formatOfflineHours(this.offlineReport.elapsedSeconds);
    }

    public get currentStarSystemName(): string {
        return STAR_SYSTEM_LABELS[this.state.prestige.currentStarSystemKey] || this.state.prestige.currentStarSystemKey;
    }

    public get currentStageProgressPercent(): number {
        const progress = this.state.prestige.currentStageProgress;
        return formatPercent(progress.requiredPlanetClears > 0 ? progress.clearedPlanetCount / progress.requiredPlanetClears : 0);
    }

    public get prestigeReady(): boolean {
        return canPrestige(this.state);
    }

    public get nextStarSystemName(): string {
        const definition = this.getPrestigeDefinitionByStage(this.state.prestige.currentStage + 1);
        return STAR_SYSTEM_LABELS[definition.starSystemKey] || definition.starSystemKey;
    }

    public get prestigeRemainingPlanetClears(): number {
        const progress = this.state.prestige.currentStageProgress;
        return Math.max(0, progress.requiredPlanetClears - progress.clearedPlanetCount);
    }

    public get completedStarSystemCount(): number {
        return this.state.prestige.completedStarSystems.length;
    }

    public get prestigeGateMessage(): string {
        if (this.prestigeReady) {
            return `${this.nextStarSystemName} 항성계로 도약할 수 있습니다. 현재 탐사 런은 초기화됩니다.`;
        }

        const requirements: string[] = [];
        if (this.prestigeRemainingPlanetClears > 0) {
            requirements.push(`행성 ${this.prestigeRemainingPlanetClears}개 추가 완료`);
        }

        if (!this.state.prestige.currentStageProgress.sGradeCleared) {
            requirements.push("S등급 행성 1회 완료");
        }

        return requirements.length > 0 ? requirements.join(" / ") : "프레스티지 조건 확인 중";
    }

    public get researchProgressPercent(): number {
        return formatPercent(this.totalResearchCount > 0 ? this.completedResearchCount / this.totalResearchCount : 0);
    }

    public get currentPlanet() {
        return this.state.run.currentExpedition.targetPlanet;
    }

    public get currentPlanetThemeClass(): string {
        const planet = this.currentPlanet;
        const type = planet?.type || "unknown";
        const lifeSignal = planet?.lifeSignal || "none";
        const hazard = planet?.hazards[0]?.type || "calm";
        return `planet-theme-${type} planet-life-${lifeSignal} planet-hazard-${hazard}`;
    }

    public get currentTutorialStep(): TutorialStep {
        return this.tutorialSteps[this.tutorialStepIndex] || this.tutorialSteps[0];
    }

    public get tutorialSteps(): TutorialStep[] {
        if (this.tutorialScope === "research") {
            return RESEARCH_TUTORIAL_STEPS;
        }

        if (this.tutorialScope === "artifact") {
            return ARTIFACT_TUTORIAL_STEPS;
        }

        if (this.tutorialScope === "record") {
            return RECORD_TUTORIAL_STEPS;
        }

        if (this.tutorialScope === "prestige") {
            return PRESTIGE_TUTORIAL_STEPS;
        }

        return TUTORIAL_STEPS;
    }

    public get tutorialStepLabel(): string {
        return `${this.tutorialStepIndex + 1} / ${this.tutorialSteps.length}`;
    }

    public get tutorialActionLabel(): string {
        return this.tutorialStepIndex >= this.tutorialSteps.length - 1 ? "완료" : "다음";
    }

    public get tutorialReplayLabel(): string {
        if (this.activeTab === "research") {
            return "연구 튜토리얼 다시보기";
        }

        if (this.activeTab === "artifacts") {
            return "유물 튜토리얼 다시보기";
        }

        if (this.activeTab === "logs") {
            return "기록 튜토리얼 다시보기";
        }

        if (this.activeTab === "prestige") {
            return "프레스티지 튜토리얼 다시보기";
        }

        return "탐사 튜토리얼 다시보기";
    }

    public isTutorialHighlight(highlight: TutorialHighlight): boolean {
        return this.showTutorial && this.currentTutorialStep.highlight === highlight;
    }

    public replayCurrentTutorial(): void {
        this.startTutorial(this.getActiveTutorialScope());
    }

    public nextTutorial(): void {
        if (this.tutorialStepIndex >= this.tutorialSteps.length - 1) {
            this.completeTutorial();
            return;
        }

        this.tutorialStepIndex += 1;
    }

    public skipTutorial(): void {
        this.completeTutorial();
    }

    public setActiveTab(tab: ActiveTab): void {
        this.activeTab = tab;
        if (tab === "research") {
            markGuideQuestFlag(this.state, "researchTreeOpened");
        } else if (tab === "artifacts") {
            markGuideQuestFlag(this.state, "artifactVaultOpened");
        } else if (tab === "prestige") {
            markGuideQuestFlag(this.state, "prestigeTabOpened");
            markGuideQuestFlag(this.state, "prestigeConditionChecked");
        }

        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
        this.maybeStartTabTutorial();
    }

    public toggleQuestPanel(event: Event): void {
        event.stopPropagation();
        this.questPanelCollapsed = !this.questPanelCollapsed;
    }

    public openQuestList(): void {
        this.showQuestList = true;
    }

    public closeQuestList(): void {
        this.showQuestList = false;
    }

    public closeOfflineReport(): void {
        this.showOfflineReport = false;
    }

    public upgradePart(specKey: ProbeSpecKey): void {
        const result = upgradeProbePart(this.state, specKey);
        const label = SPEC_LABELS[specKey].name;
        this.lastActionMessage = result.ok ? `${label} 강화 완료` : result.message;
        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
    }

    public research(nodeId: ResearchNodeId): void {
        const result = completeResearch(this.state, nodeId, new Date());
        const definition = RESEARCH_DEFINITIONS[nodeId];
        this.lastActionMessage = result.ok ? `${definition.name.toUpperCase()} RESEARCH COMPLETE` : result.message;
        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
    }

    public selectArtifact(key: ArtifactKey): void {
        this.selectedArtifactKey = key;
        this.selectedArtifactView = this.artifactViews.find((artifact) => artifact.key === key) || null;
    }

    public selectLog(id: string): void {
        this.selectedLogId = id;
        this.selectedLogEntry = this.logEntries.find((entry) => entry.id === id) || null;
    }

    public getLifeSignalLabel(signal: DiscoveredPlanetRecord["lifeSignal"]): string {
        return LIFE_SIGNAL_LABELS[signal] || signal;
    }

    public requestPrestige(): void {
        markGuideQuestFlag(this.state, "prestigeConditionChecked");
        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
        this.showPrestigeDialog = true;
        this.prestigeDialogMode = "confirm";
        this.prestigeMonologue = "";
    }

    public cancelPrestige(): void {
        this.showPrestigeDialog = false;
        this.prestigeMonologue = "";
    }

    public confirmPrestige(): void {
        const previousStage = this.state.prestige.currentStage;
        const previousSystem = this.currentStarSystemName;
        const result = applyPrestige(this.state, new Date());
        this.lastActionMessage = result.message;
        if (result.ok) {
            this.prestigeDialogMode = "monologue";
            this.prestigeMonologue = this.createPrestigeMonologue(previousStage, previousSystem);
        }

        this.offlineReport = this.state.offline.lastOfflineReport;
        this.evaluateQuestCompletions();
        this.refreshViewModels();
        saveGameState(this.state);
    }

    public closePrestigeDialog(): void {
        this.showPrestigeDialog = false;
        this.prestigeMonologue = "";
    }

    public saveNow(): void {
        saveGameState(this.state);
        this.lastActionMessage = "GAME STATE SAVED";
    }

    public resetGame(): void {
        this.handleDevelopmentReset();
    }

    public trackById(_: number, item: { id?: string | number; key?: string | number }): string | number {
        return item.id || item.key || "";
    }

    private startAutoTick(): void {
        this.stopAutoTick();
        this.tickTimer = setInterval(() => {
            this.tick();
        }, TICK_INTERVAL_MS);
        this.saveTimer = setInterval(() => {
            saveGameState(this.state);
        }, AUTO_SAVE_INTERVAL_MS);
    }

    private stopAutoTick(): void {
        if (this.tickTimer) {
            clearInterval(this.tickTimer);
            this.tickTimer = null;
        }

        if (this.saveTimer) {
            clearInterval(this.saveTimer);
            this.saveTimer = null;
        }
    }

    private startShipAnimation(): void {
        if (typeof window === "undefined") {
            return;
        }

        this.stopShipAnimation();
        this.shipAnimationStart = 0;

        const animate = (timestamp: number): void => {
            if (!this.shipAnimationStart) {
                this.shipAnimationStart = timestamp;
            }

            const cycleElapsed = (timestamp - this.shipAnimationStart) % this.shipFlightMs;
            const progress = cycleElapsed / this.shipFlightMs;
            this.shipX = 14 + 66 * progress;
            this.shipY = 78 - 55 * progress;
            const remainingSeconds = Math.max(0, Math.ceil((this.shipFlightMs - cycleElapsed) / 1000));
            this.shipEtaText = `ETA 00:${remainingSeconds.toString().padStart(2, "0")}`;
            this.animationFrameId = window.requestAnimationFrame(animate);
        };

        this.animationFrameId = window.requestAnimationFrame(animate);
    }

    private stopShipAnimation(): void {
        if (typeof window === "undefined" || this.animationFrameId === null) {
            return;
        }

        window.cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
    }

    private initializeTutorial(): void {
        if (typeof window === "undefined") {
            return;
        }

        if (this.shouldRunTutorial("main")) {
            this.startTutorial("main");
            return;
        }

        this.showTutorial = false;
        this.tutorialStepIndex = 0;
        this.maybeStartTabTutorial();
    }

    private completeTutorial(): void {
        const completedScope = this.tutorialScope;
        this.showTutorial = false;
        this.tutorialStepIndex = 0;
        this.completedTutorialScopes.add(completedScope);

        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(this.getTutorialStorageKey(completedScope), "true");
            } catch {
                // Ignore storage failures; the tutorial still closes for this session.
            }
        }

        if (completedScope === "main") {
            this.maybeStartTabTutorial();
        }
    }

    private startTutorial(scope: TutorialScope): void {
        this.tutorialScope = scope;
        this.tutorialStepIndex = 0;
        this.showTutorial = true;
    }

    private getActiveTutorialScope(): TutorialScope {
        if (this.activeTab === "research") {
            return "research";
        }

        if (this.activeTab === "artifacts") {
            return "artifact";
        }

        if (this.activeTab === "logs") {
            return "record";
        }

        if (this.activeTab === "prestige") {
            return "prestige";
        }

        return "main";
    }

    private maybeStartTabTutorial(): void {
        if (this.showTutorial) {
            return;
        }

        if (this.activeTab === "research" && this.shouldRunTutorial("research")) {
            this.startTutorial("research");
            return;
        }

        if (this.activeTab === "artifacts" && this.shouldRunTutorial("artifact")) {
            this.startTutorial("artifact");
            return;
        }

        if (this.activeTab === "logs" && this.shouldRunTutorial("record")) {
            this.startTutorial("record");
            return;
        }

        if (this.activeTab === "prestige" && this.shouldRunTutorial("prestige")) {
            this.startTutorial("prestige");
        }
    }

    private shouldRunTutorial(scope: TutorialScope): boolean {
        if (typeof window === "undefined" || this.completedTutorialScopes.has(scope)) {
            return false;
        }

        try {
            return window.localStorage.getItem(this.getTutorialStorageKey(scope)) !== "true";
        } catch {
            return true;
        }
    }

    private getTutorialStorageKey(scope: TutorialScope): string {
        if (scope === "research") {
            return RESEARCH_TUTORIAL_STORAGE_KEY;
        }

        if (scope === "artifact") {
            return ARTIFACT_TUTORIAL_STORAGE_KEY;
        }

        if (scope === "record") {
            return RECORD_TUTORIAL_STORAGE_KEY;
        }

        if (scope === "prestige") {
            return PRESTIGE_TUTORIAL_STORAGE_KEY;
        }

        return TUTORIAL_STORAGE_KEY;
    }

    private tick(): void {
        const lastTickMs = Date.parse(this.state.run.currentExpedition.lastTickAt);
        const startAt = Number.isNaN(lastTickMs) ? new Date() : new Date(lastTickMs);
        const summary = advanceGameByElapsedMs(this.state, TICK_INTERVAL_MS, startAt);
        this.evaluateQuestCompletions();
        this.refreshViewModels();
        if (summary.planetsDiscovered > 0) {
            this.showExpeditionResult(summary);
        }
    }

    private refreshViewModels(): void {
        this.latestPlanet = getLatestPlanet(this.state);
        this.resourceViews = this.buildResourceViews();
        this.specViews = this.buildSpecViews();
        this.researchTree = this.buildResearchTreeNode("basic_exploration");
        this.refreshResearchSummary();
        this.artifactViews = this.buildArtifactViews();
        this.selectedArtifactView = this.artifactViews.find((artifact) => artifact.key === this.selectedArtifactKey) || this.artifactViews[0] || null;
        this.artifactLogEntries = this.buildArtifactLogEntries();
        this.logEntries = this.buildLogEntries();
        this.recordSummaryViews = this.buildRecordSummaryViews();
        this.refreshSelectedLogEntry();
        this.prestigeLogEntries = this.buildPrestigeLogEntries();
        this.refreshPrestigeSummary();
        this.refreshQuestViews();
    }

    private refreshQuestViews(): void {
        this.completedQuestCount = getGuideQuestCompletedCount(this.state);
        this.totalQuestCount = GUIDE_QUEST_DEFINITIONS.length;
        this.questCategoryViews = GUIDE_QUEST_CATEGORIES.map((category) => {
            const quests = GUIDE_QUEST_DEFINITIONS
                .filter((quest) => quest.category === category.id)
                .map((quest) => this.buildQuestView(quest));

            return {
                id: category.id,
                name: category.name,
                icon: category.icon,
                completedCount: quests.filter((quest) => quest.status === "completed").length,
                totalCount: quests.length,
                quests
            };
        });

        const activeQuest = getActiveGuideQuestDefinition(this.state) || GUIDE_QUEST_DEFINITIONS[GUIDE_QUEST_DEFINITIONS.length - 1];
        this.activeQuestView = activeQuest ? this.buildQuestView(activeQuest) : null;
    }

    private buildQuestView(quest: GuideQuestDefinition): QuestView {
        const category = getGuideQuestCategory(quest.category);
        const progress = getGuideQuestProgress(this.state, quest);
        return {
            id: quest.id,
            title: quest.title,
            categoryName: category.name,
            categoryIcon: category.icon,
            status: getGuideQuestStatus(this.state, quest),
            progress,
            progressLabel: progress.label,
            rewardLabel: formatGuideQuestRewardLabel(quest.reward)
        };
    }

    private evaluateQuestCompletions(): GuideQuestCompletion[] {
        const completions = evaluateGuideQuests(this.state);
        if (completions.length > 0) {
            this.showQuestCompletion(completions[completions.length - 1]);
            this.lastActionMessage = `QUEST COMPLETE: ${completions[completions.length - 1].title}`;
        }

        return completions;
    }

    private showQuestCompletion(completion: GuideQuestCompletion): void {
        this.questCompletionPopup = {
            title: `#${completion.questId} ${completion.title}`,
            categoryName: completion.categoryName,
            rewardLabel: completion.rewardLabel,
            progressLabel: `${completion.completedCount} / ${completion.totalCount}`
        };
        this.questPanelPulse = true;

        if (this.questCompletionTimer) {
            clearTimeout(this.questCompletionTimer);
        }

        this.questCompletionTimer = setTimeout(() => {
            this.questCompletionPopup = null;
            this.questPanelPulse = false;
            this.questCompletionTimer = null;
        }, 3200);
    }

    private buildResourceViews(): ResourceView[] {
        const stats = this.state.run.probe.cachedEffectiveStats;
        return [
            {
                key: "explorationData",
                name: "탐사 데이터",
                amount: this.formatCompactValue(this.state.run.resources.explorationData.amount),
                detail: "연구 트리",
                rate: `+${this.formatCompactValue(4.5 * stats.resourceGainMultiplier)}/s`,
                tone: "data"
            },
            {
                key: "energy",
                name: "에너지",
                amount: `${this.formatCompactValue(this.state.run.resources.energy.current)} / ${this.formatCompactValue(this.state.run.resources.energy.capacity)}`,
                detail: "탐사 연료",
                rate: `+${this.formatCompactValue(0.035)}/s`,
                tone: "energy"
            },
            {
                key: "parts",
                name: "부품",
                amount: this.formatCompactValue(this.state.run.resources.parts.amount),
                detail: "스펙 강화",
                rate: `+${this.formatCompactValue(0.04 * stats.resourceGainMultiplier)}/s`,
                tone: "parts"
            }
        ];
    }

    private buildSpecViews(): SpecView[] {
        const stats = this.state.run.probe.cachedEffectiveStats;
        const highlightedSpecKey = this.getHighlightedSpecKey();
        return (Object.keys(PART_UPGRADE_DEFINITIONS) as ProbeSpecKey[]).map((key) => {
            const cost = getPartUpgradeCost(this.state, key);
            return {
                key,
                name: SPEC_LABELS[key].name,
                shortName: SPEC_LABELS[key].shortName,
                level: this.state.run.probe.specs[key].level,
                cost: this.formatCompactValue(cost),
                canUpgrade: this.state.run.resources.parts.amount >= cost,
                highlighted: highlightedSpecKey === key,
                effectLabel: this.getSpecEffectLabel(key, stats)
            };
        });
    }

    private buildResearchTreeNode(id: ResearchNodeId): ResearchTreeNode {
        const definition = RESEARCH_DEFINITIONS[id];
        const node = this.state.run.research.nodes[id];
        const children = (RESEARCH_TREE[id] || []).map((childId) => this.buildResearchTreeNode(childId));
        return {
            id,
            name: this.getResearchName(id),
            description: this.getResearchDescription(id),
            status: node.status,
            statusLabel: this.getResearchStatusLabel(node.status),
            cost: this.formatCompactValue(definition.costExplorationData),
            canResearch: node.status === "available" && this.state.run.resources.explorationData.amount >= definition.costExplorationData,
            branchLabel: this.getResearchBranchLabel(id),
            branchTone: this.getResearchBranchTone(id),
            completedChildren: children.filter((child) => child.status === "completed").length,
            totalChildren: children.length,
            children
        };
    }

    private refreshResearchSummary(): void {
        const nodes = Object.values(this.state.run.research.nodes);
        this.completedResearchCount = nodes.filter((node) => node.status === "completed").length;
        this.availableResearchCount = nodes.filter((node) => node.status === "available").length;
        this.researchEffectViews = this.buildResearchEffectViews();
    }

    private buildResearchEffectViews(): ResearchEffectView[] {
        const effects = this.state.run.research.activeEffects;
        return [
            {
                key: "speed",
                label: "탐사 속도",
                value: `x${effects.explorationSpeedMultiplier.toFixed(2)}`,
                active: effects.explorationSpeedMultiplier > 1
            },
            {
                key: "gain",
                label: "자원 획득",
                value: `x${effects.resourceGainMultiplier.toFixed(2)}`,
                active: effects.resourceGainMultiplier > 1
            },
            {
                key: "deep",
                label: "심우주",
                value: effects.deepSpaceUnlocked ? "ON" : "LOCK",
                active: effects.deepSpaceUnlocked
            },
            {
                key: "life",
                label: "생명 감지",
                value: `+${Math.round(effects.lifeDiscoveryChanceBonus * 100)}%`,
                active: effects.lifeDiscoveryChanceBonus > 0
            },
            {
                key: "artifact",
                label: "유물 감지",
                value: `+${Math.round(effects.artifactDiscoveryChanceBonus * 100)}%`,
                active: effects.artifactDiscoveryChanceBonus > 0
            }
        ];
    }

    private buildArtifactViews(): ArtifactView[] {
        const records = Object.values(this.state.archive.artifactCollection.byInstanceId);
        return ARTIFACT_DEFINITIONS.map((definition) => {
            const summary = this.state.archive.artifactCollection.summaryByArtifactKey[definition.key];
            const firstRecord = records.find((record) => record.artifactKey === definition.key) || null;
            const sourcePlanetId = firstRecord?.sourcePlanetId || summary?.sourcePlanetIds[0] || "";
            return {
                ...definition,
                discovered: Boolean(summary && summary.count > 0),
                count: summary?.count || 0,
                rarity: firstRecord ? this.getRarityLabel(firstRecord) : "미발견",
                foundAt: firstRecord?.foundAt || "",
                sourcePlanet: sourcePlanetId ? this.state.archive.discoveredPlanets.byId[sourcePlanetId] || null : null
            };
        });
    }

    private buildArtifactLogEntries(): ArtifactLogEntryView[] {
        return Object.values(this.state.archive.artifactCollection.byInstanceId)
            .slice()
            .sort((left, right) => Date.parse(right.foundAt) - Date.parse(left.foundAt))
            .slice(0, 8)
            .map((record) => {
                const definition = this.getArtifactDefinition(record.artifactKey);
                const sourcePlanet = this.state.archive.discoveredPlanets.byId[record.sourcePlanetId] || null;
                const sourceLabel = sourcePlanet ? `${sourcePlanet.name} · ${sourcePlanet.grade}` : "UNKNOWN";
                return {
                    id: record.artifactInstanceId,
                    name: definition?.name || record.artifactKey,
                    iconClass: definition?.iconClass || "",
                    meta: `${this.getRarityLabel(record)} / ${sourceLabel} / STAGE ${record.prestigeStageFound}`,
                    foundAt: this.formatDateShort(record.foundAt)
                };
            });
    }

    private buildLogEntries(): LogEntryView[] {
        return this.state.archive.discoveredPlanets.order
            .slice()
            .reverse()
            .slice(0, 24)
            .map((planetId) => this.state.archive.discoveredPlanets.byId[planetId])
            .filter((record): record is DiscoveredPlanetRecord => Boolean(record))
            .map((record) => {
                const artifactLabel = record.artifactInstanceIds.length > 0 ? ` / 유물 ${record.artifactInstanceIds.length}` : "";
                const typeLabel = PLANET_TYPE_LABELS[record.type] || record.type;
                const hazardLabel = record.hazards.length > 0
                    ? record.hazards.map((hazard) => HAZARD_LABELS[hazard] || hazard).join(" / ")
                    : "안정";
                return {
                    id: record.planetId,
                    title: record.name,
                    meta: `${record.grade}등급 ${typeLabel} / ${this.getOutcomeLabel(record.outcome)}`,
                    result: `DATA +${this.formatCompactValue(record.earnedResources.explorationData)} / PARTS +${this.formatCompactValue(record.earnedResources.parts)}${artifactLabel}`,
                    statusLabel: this.getOutcomeLabel(record.outcome),
                    outcome: record.outcome,
                    grade: record.grade,
                    typeLabel,
                    lifeSignalLabel: LIFE_SIGNAL_LABELS[record.lifeSignal] || record.lifeSignal,
                    hazardLabel,
                    discoveredAt: this.formatDateShort(record.discoveredAt),
                    completedAt: this.formatDateShort(record.completedAt || record.discoveredAt),
                    dataGained: `+${this.formatCompactValue(record.earnedResources.explorationData)} DATA`,
                    partsGained: `+${this.formatCompactValue(record.earnedResources.parts)} PARTS`,
                    artifactCount: record.artifactInstanceIds.length,
                    seed: record.seed
                };
            });
    }

    private buildRecordSummaryViews(): RecordSummaryView[] {
        const records = Object.values(this.state.archive.discoveredPlanets.byId);
        const completedCount = records.filter((record) => record.outcome === "completed").length;
        const failedCount = records.filter((record) => record.outcome === "failed").length;
        const successRate = records.length > 0 ? `${formatPercent(completedCount / records.length)}%` : "0%";
        const highGradeCount = records.filter((record) => record.grade === "S" || record.grade === "X").length;

        return [
            {
                key: "total",
                label: "LOGS",
                value: this.formatCompactValue(records.length),
                tone: "data"
            },
            {
                key: "success",
                label: "CLEAR",
                value: successRate,
                tone: "success"
            },
            {
                key: "failed",
                label: "FAIL",
                value: this.formatCompactValue(failedCount),
                tone: "danger"
            },
            {
                key: "rare",
                label: "S/X",
                value: this.formatCompactValue(highGradeCount),
                tone: "rare"
            }
        ];
    }

    private refreshSelectedLogEntry(): void {
        if (this.logEntries.length === 0) {
            this.selectedLogId = "";
            this.selectedLogEntry = null;
            return;
        }

        const selected = this.logEntries.find((entry) => entry.id === this.selectedLogId) || this.logEntries[0];
        this.selectedLogId = selected.id;
        this.selectedLogEntry = selected;
    }

    private buildPrestigeLogEntries(): PrestigeLogEntryView[] {
        return this.state.prestige.history
            .slice()
            .reverse()
            .slice(0, 6)
            .map((entry) => ({
                id: `prestige-${entry.stage}-${entry.prestigedAt}`,
                stageLabel: `STAGE ${entry.stage}`,
                systemName: STAR_SYSTEM_LABELS[entry.starSystemKey] || entry.starSystemKey,
                bonusLabel: this.getPrestigeBonusLabel(entry.grantedBonuses),
                date: this.formatDateShort(entry.prestigedAt)
            }));
    }

    private refreshPrestigeSummary(): void {
        this.prestigeRequirementViews = this.buildPrestigeRequirementViews();
        this.prestigeBonusViews = this.buildPrestigeBonusViews();
        this.prestigeRewardViews = this.buildPrestigeRewardViews();
    }

    private buildPrestigeRequirementViews(): PrestigeRequirementView[] {
        const progress = this.state.prestige.currentStageProgress;
        return [
            {
                key: "planet-clears",
                label: "행성 완료",
                value: `${this.formatCompactValue(progress.clearedPlanetCount)} / ${this.formatCompactValue(progress.requiredPlanetClears)}`,
                detail: `${this.prestigeRemainingPlanetClears > 0 ? this.formatCompactValue(this.prestigeRemainingPlanetClears) : "0"}개 남음`,
                complete: progress.clearedPlanetCount >= progress.requiredPlanetClears
            },
            {
                key: "s-grade",
                label: "S등급 완료",
                value: progress.sGradeCleared ? "완료" : "대기",
                detail: "S등급 행성 1회",
                complete: progress.sGradeCleared
            }
        ];
    }

    private buildPrestigeBonusViews(): PrestigeBonusView[] {
        const bonuses = this.state.prestige.permanentBonuses;
        return [
            {
                key: "speed",
                label: "탐사 속도",
                value: `x${bonuses.explorationSpeedMultiplier.toFixed(2)}`,
                active: bonuses.explorationSpeedMultiplier > 1
            },
            {
                key: "gain",
                label: "자원 획득",
                value: `x${bonuses.resourceGainMultiplier.toFixed(2)}`,
                active: bonuses.resourceGainMultiplier > 1
            },
            {
                key: "artifact",
                label: "유물 감지",
                value: `+${Math.round(bonuses.artifactDiscoveryChanceBonus * 100)}%`,
                active: bonuses.artifactDiscoveryChanceBonus > 0
            },
            {
                key: "x-grade",
                label: "X등급 행성",
                value: bonuses.xGradeUnlocked ? "OPEN" : "LOCK",
                active: bonuses.xGradeUnlocked
            }
        ];
    }

    private buildPrestigeRewardViews(): PrestigeBonusView[] {
        const definition = this.getPrestigeDefinitionByStage(this.state.prestige.currentStage);
        const rewards: PrestigeBonusView[] = [];

        if (definition.grantedBonuses.explorationSpeedMultiplier) {
            rewards.push({
                key: "reward-speed",
                label: "탐사 속도",
                value: `x${definition.grantedBonuses.explorationSpeedMultiplier.toFixed(2)}`,
                active: true
            });
        }

        if (definition.grantedBonuses.resourceGainMultiplier) {
            rewards.push({
                key: "reward-gain",
                label: "자원 획득",
                value: `x${definition.grantedBonuses.resourceGainMultiplier.toFixed(2)}`,
                active: true
            });
        }

        if (definition.grantedBonuses.artifactDiscoveryChanceBonus) {
            rewards.push({
                key: "reward-artifact",
                label: "유물 감지",
                value: `+${Math.round(definition.grantedBonuses.artifactDiscoveryChanceBonus * 100)}%`,
                active: true
            });
        }

        if (definition.grantedBonuses.xGradeUnlocked) {
            rewards.push({
                key: "reward-x-grade",
                label: "X등급 행성",
                value: "OPEN",
                active: true
            });
        }

        return rewards.length > 0 ? rewards : [{ key: "reward-memory", label: "항해 기록", value: "유지", active: true }];
    }

    private showExpeditionResult(summary: { resourcesGained: ResourceAmounts; artifactInstanceIds: string[] }): void {
        const planet = this.latestPlanet;
        if (!planet) {
            return;
        }

        const failed = planet.outcome === "failed";
        this.recentResult = {
            title: failed ? "탐사 실패" : "탐사 완료",
            planetName: planet.name,
            detail: failed
                ? "방어막 출력 부족. 부품 일부를 잃고 귀환했습니다."
                : `+${this.formatCompactValue(summary.resourcesGained.explorationData)} DATA / +${this.formatCompactValue(summary.resourcesGained.parts)} PARTS / 유물 ${summary.artifactInstanceIds.length}`,
            failed
        };
        this.lastActionMessage = failed ? "SHIELD DEFICIENCY DETECTED" : "EXPEDITION RESULT RECEIVED";

        if (this.resultTimer) {
            clearTimeout(this.resultTimer);
        }
        this.resultTimer = setTimeout(() => {
            this.recentResult = null;
            this.resultTimer = null;
        }, 3600);
    }

    private getHighlightedSpecKey(): ProbeSpecKey | null {
        if (this.currentRequiredShieldPower > this.state.run.probe.cachedEffectiveStats.survivalPower) {
            return "shield";
        }

        if (this.latestPlanet?.outcome === "failed") {
            return "shield";
        }

        return null;
    }

    private getSpecEffectLabel(specKey: ProbeSpecKey, stats: GameState["run"]["probe"]["cachedEffectiveStats"]): string {
        if (specKey === "engine") {
            return `속도 x${stats.explorationSpeedMultiplier}`;
        }

        if (specKey === "explorationGear") {
            return `획득 x${stats.resourceGainMultiplier}`;
        }

        if (specKey === "shield") {
            return `생존 ${stats.survivalPower}`;
        }

        return `유물 ${Math.round(stats.rareDiscoveryChanceBonus * 100)}%`;
    }

    private buildOfflineReportLines(report: OfflineReport): string[] {
        return createOfflineReportLines(report);
    }

    private createPrestigeMonologue(stage: number, systemName: string): string {
        const completed = PRESTIGE_DEFINITIONS.find((definition) => definition.stage === stage);
        const bonusLabel = completed?.grantedBonuses.resourceGainMultiplier
            ? "자원 신호가 더 선명해졌다."
            : completed?.grantedBonuses.artifactDiscoveryChanceBonus
                ? "오래된 기억을 더 잘 듣게 되었다."
                : completed?.grantedBonuses.xGradeUnlocked
                    ? "금지된 좌표가 침묵을 멈췄다."
                    : "엔진은 같은 어둠을 조금 더 빨리 가른다.";
        return `${systemName}의 별빛을 뒤로 둔다. 나는 초기화되지만, 잃어버린 지구의 방향은 사라지지 않는다. ${bonusLabel}`;
    }

    private getResearchName(id: ResearchNodeId): string {
        const names: Partial<Record<ResearchNodeId, string>> = {
            basic_exploration: "기초 탐사술",
            planet_analysis: "행성 분석",
            high_speed_engine: "고속 엔진",
            deep_space_exploration: "심우주 탐사",
            life_detection: "생명체 감지",
            artifact_scanner: "유물 스캐너"
        };
        return names[id] || RESEARCH_DEFINITIONS[id].name;
    }

    private getResearchDescription(id: ResearchNodeId): string {
        const descriptions: Partial<Record<ResearchNodeId, string>> = {
            basic_exploration: "첫 연구 분기 개방",
            planet_analysis: "자원 획득량 +20%",
            high_speed_engine: "탐사 속도 +30%",
            deep_space_exploration: "더 먼 행성 탐사 가능",
            life_detection: "생명체 발견 확률 +50%",
            artifact_scanner: "유물 발견 확률 +40%"
        };
        return descriptions[id] || RESEARCH_DEFINITIONS[id].description;
    }

    private getResearchBranchLabel(id: ResearchNodeId): string {
        const labels: Partial<Record<ResearchNodeId, string>> = {
            basic_exploration: "CORE",
            planet_analysis: "RESOURCE",
            high_speed_engine: "VELOCITY",
            deep_space_exploration: "DISCOVERY"
        };
        return labels[id] || "NODE";
    }

    private getResearchBranchTone(id: ResearchNodeId): string {
        const tones: Partial<Record<ResearchNodeId, string>> = {
            basic_exploration: "core",
            planet_analysis: "resource",
            high_speed_engine: "speed",
            deep_space_exploration: "discovery"
        };
        return tones[id] || "core";
    }

    private getResearchStatusLabel(status: ResearchStatus): string {
        const labels: Record<ResearchStatus, string> = {
            locked: "잠김",
            available: "가능",
            researching: "진행",
            completed: "완료"
        };
        return labels[status];
    }

    private getRarityLabel(record: ArtifactRecord): string {
        const labels: Record<ArtifactRecord["rarity"], string> = {
            uncommon: "비범",
            rare: "희귀",
            epic: "서사",
            legendary: "전설"
        };
        return labels[record.rarity];
    }

    private getArtifactDefinition(key: ArtifactKey): ArtifactDefinition | undefined {
        return ARTIFACT_DEFINITIONS.find((definition) => definition.key === key);
    }

    private getOutcomeLabel(outcome: DiscoveredPlanetRecord["outcome"]): string {
        const labels: Record<DiscoveredPlanetRecord["outcome"], string> = {
            completed: "완료",
            failed: "실패",
            in_progress: "진행"
        };
        return labels[outcome];
    }

    private getPrestigeBonusLabel(bonuses: Partial<GameState["prestige"]["permanentBonuses"]>): string {
        const labels: string[] = [];
        if (bonuses.explorationSpeedMultiplier) {
            labels.push(`속도 x${bonuses.explorationSpeedMultiplier}`);
        }

        if (bonuses.resourceGainMultiplier) {
            labels.push(`자원 x${bonuses.resourceGainMultiplier}`);
        }

        if (bonuses.artifactDiscoveryChanceBonus) {
            labels.push(`유물 +${Math.round(bonuses.artifactDiscoveryChanceBonus * 100)}%`);
        }

        if (bonuses.xGradeUnlocked) {
            labels.push("X등급 개방");
        }

        return labels.length > 0 ? labels.join(" / ") : "기록 유지";
    }

    private getPrestigeDefinitionByStage(stage: number): typeof PRESTIGE_DEFINITIONS[number] {
        return PRESTIGE_DEFINITIONS.find((definition) => definition.stage === stage) || PRESTIGE_DEFINITIONS[PRESTIGE_DEFINITIONS.length - 1];
    }

    private formatCompactValue(value: number): string {
        return formatCompactResourceValue(value);
    }

    private formatDateShort(value: string): string {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "-";
        }

        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        return `${month}.${day} ${hours}:${minutes}`;
    }
}

export default PageHomeComponent;